import { Typography } from "@/components/atoms";
import { Skeleton } from "@/components/atoms/skeleton";
import { AppointmentCalendarIcon } from "@/components/atoms/icons/AppointmentCalendarIcon";
import { CalendarDaysIcon } from "@/components/atoms/icons/CalendarDaysIcon";
import { LayersIcon } from "@/components/atoms/icons/LayersIcon";
import { UnitReservedConfettiIcon } from "@/components/atoms/icons/UnitReservedConfettiIcon";
import { OkDialog } from "@/components/molecules/dialogs/OkDialog";
import { FullScreenLoader } from "@/components/molecules/FullScreenLoader";
import WebAppLayout from "@/components/organisms/WebAppLayout";
import { useWebDocumentTitle } from "@/core/hooks/useWebDocumentTitle";

import { UI_PRIMITIVES } from "@/core/constants/uiColors";
import { getWebPageContentPadding } from "@/core/constants/webLayout";
import type { BookingResponse } from "@/core/api/models/bookings";
import type { ProfileDetailsResponse } from "@/core/api/models/profile";
import type { AvailableUnit, UnitDetailsData } from "@/core/api/models/properties";
import {
  useReservationActions,
  useSalesAgentContact,
  useTranslation,
  useUIColors,
  useWebViewport,
} from "@/core/hooks";
import { useI18nContext } from "@/core/providers/I18nProvider";
import { BREAKPOINTS } from "@/core/utils/responsive";
import {
  RESERVATION_STATUS_CONFIG,
  type ReservationStatus,
} from "@/core/reservation";
import { fetchPropertyAssets } from '@/core/services/contentfulClient';
import { isAdhaProfileActionEnabled } from '@/modules/properties-web/utils/adhaProfileActions';
import { isAppointmentBookingWindowClosed } from '@/modules/book-appointment/utils/slotDates';
import type { ExploreVrBannerData, PropertyImage } from '@/core/services/contentfulClient';
import {
  resolveBrochureUrlsFromAssets,
  resolveDocumentDownloadUrls,
} from '@/core/utils/brochureDownload';
import {
  useAppStore,
  type ReservedUnit,
} from "@/core/stores/appStore";
import { ALMETLAA_ACTION, ALMETLAA_SCREEN } from '@/core/constants/dynatraceCatalog';
import { getUnitDetailsLayout } from '@/modules/properties-web/details/unitDetailsLayout';
import { logger } from '@/core/logger';
import {
  trackAdhaReservationCardClick,
  trackCatalogEvent,
  trackProjectPromoBannerClick,
  trackReservationSummaryOpen,
} from '@/core/services/trackDynatraceCatalog';
import { useCatalogDynatraceView } from '@/core/hooks/useCatalogDynatraceView';
import { formatNamedGreeting, resolveGreetingPrefix } from '@/core/utils/greeting';
import {
  AppointmentFlowDialogs,
  SHOW_SALES_AGENT_CONTACT,
  useBookAppointmentFlow,
  type BookedAppointment,
} from "@/modules/book-appointment";

import { useIsFocused } from "@react-navigation/native";
import { useQuery } from '@tanstack/react-query';
import { router } from "expo-router";
import React, { startTransition, useDeferredValue, useEffect, useMemo, useState } from "react";
import { Linking, Platform, Pressable, ScrollView, StyleSheet, Text, View, type DimensionValue, type StyleProp, type ViewStyle } from "react-native";
import { CancelReservationDialog } from "../components/CancelReservationDialog";
import { AppointmentSalesContact } from "../components/AppointmentSalesContact";
import { ListingCard } from "../components/ListingCard";
import { ListingEmptyState } from "../components/ListingEmptyState";
import { PRICE_SLIDER_MAX_M, PriceRangeSlider } from "../components/PriceRangeSlider";
import { ReservationCanceledDialog } from "../components/ReservationCanceledDialog";
import { ReservationStatusMessage } from "../components/ReservationStatusMessage";
import { AppointmentCardSkeleton, ListingCardSkeleton } from "../components/skeleton";
import { UnitListingInlineFilters } from "../components/UnitListingInlineFilters";
import { useUnitListingFilterStore } from "../store/unitListingFilterStore";
import {
  bedroomSelectionKey,
  defaultSliderMillionsForBedrooms,
  filterUnitsByBedrooms,
  type BedroomFilterOption,
} from "../utils/unitListingFilters";
import type { ListingLabels, PropertyTranslateFn } from "../constants/getPropertyLabels";
import {
  resolveWebReservationMappedStatus,
} from "../constants/webReservationStatus";
import { UNITS_NO_PRICE_LIMIT_AED, useAvailableUnits } from "../hooks/useAvailableUnits";
import { usePropertyLabels } from "../hooks/usePropertyLabels";
import { useScrollPagination } from "../hooks/useScrollPagination";
import { extractBookingDetails } from "../services/bookingService";
import { resolveIskanLoanAmountAed } from "../utils/iskanLoan";
import {
  availableUnitFromBookingRecord,
  parseActiveBookingFromApi,
  resolveReservationStatusFromBookingAndProfile,
} from "../utils/syncAdhaBookingFromApi";
import { AdhaListingLayoutShell } from "./AdhaListingLayoutShell";
import { AdhaListingProjectPanel } from "./AdhaListingProjectPanel";
import { normalizeAmenitiesList } from '@/modules/property-details/services/amenityService';
import type { GuestUnitDetailsModel, GuestUnitDocument } from "@/modules/guest-web/types/guest.types";
import { openGuestDocument } from "@/modules/guest-web/services/openGuestDocument";
import {
  ADHA_LISTING_COMPACT_PADDING_X,
  ADHA_LISTING_MAIN_FLEX,
  ADHA_LISTING_SIDEBAR_FLEX,
  ADHA_UNITS_GRID_GAP,
  getAdhaListingLayout,
  getAdhaUnitsColumnCount,
  getAdhaUnitsColumnWidthPx,
  type AdhaListingTab,
} from "./adhaListingLayout";
import { listingUnitTitle, useAdhaReservationStatus, type ListingUnit } from "./useAdhaReservationStatus";
import {
  createListingViewportStore,
  LISTING_SLOT_BLOCK_SIZE,
  ListingViewportContext,
  useViewportListingCards,
} from "../hooks/useViewportListingCards";

/** Listing grid / status-row tablet band (develop: 1100). Not ADHA tab→split (1145). */
const TABLET_MIN_WIDTH = 768;
/** Stable empty lookup so unit galleries are not resolved before Contentful succeeds. */
const EMPTY_UNIT_IMAGE_LOOKUP: Record<string, PropertyImage[]> = {};
const EMPTY_CARD_IMAGES: { uri: string }[] = [];
const TABLET_MAX_WIDTH = 1100;
/** Status + price sit side-by-side at/above this width; below stacks vertically. */
const STATUS_ROW_SIDE_BY_SIDE_MIN_WIDTH = TABLET_MAX_WIDTH;
/** Status carousel pagination (bars under multi-card). */
const STATUS_CAROUSEL_DOTS_BLOCK_HEIGHT = 20;
/** 1100–1320: side-by-side row with compact action sizing. */
const STATUS_ROW_NARROW_MAX_WIDTH = 1320;
/** Below this width: Cancel left / outline right on listing status cards. */
const STATUS_ACTIONS_SPREAD_MAX_WIDTH = 500;
/** Below this width: tightest action sizing (Z Fold, iPhone SE). */
const RESERVATION_ACTIONS_TIGHT_MAX_WIDTH = 400;
const STATUS_ROW_MATCHED_MIN_HEIGHT = 160;
/** Gap between the pinned search / bedrooms card and "Available Units". */
const FILTERS_TO_COUNT_GAP = 12;
/** `AdhaListingLayoutShell` desktopRoot `gap` between the status row and the split. */
const DESKTOP_ROOT_ROW_GAP = 10;

type ReservationActionSize = 'default' | 'compact' | 'tight';

type StatusRowLayout = {
  stackStatusRow: boolean;
  narrowStatusRow: boolean;
  matchStatusRowHeights: boolean;
  /** <500: Cancel left / outline right on listing status cards. */
  spreadStatusActions: boolean;
  /** 160px floor for side-by-side rows below wide-desktop breakpoint only. */
  statusRowMinHeight: number | undefined;
  /** width > 1320: compact content-hugging cards beside price slider. */
  useWideDesktopCompactHeights: boolean;
  reservationActionSize: ReservationActionSize;
};

function resolveReservationActionSize(width: number): ReservationActionSize {
  if (width > STATUS_ROW_NARROW_MAX_WIDTH) {
    return 'default';
  }
  if (width < RESERVATION_ACTIONS_TIGHT_MAX_WIDTH) {
    return 'tight';
  }
  return 'compact';
}

function resolveStatusRowMinHeight(
  matchStatusRowHeights: boolean,
  useWideDesktopCompactHeights: boolean,
): number | undefined {
  // Side-by-side rows (including ADHA desktop split) share a floor with the
  // price slider below the wide-desktop breakpoint. Stacked compact tabs and
  // wide desktop (>1320) hug/stretch without a forced min height.
  if (matchStatusRowHeights && !useWideDesktopCompactHeights) {
    return STATUS_ROW_MATCHED_MIN_HEIGHT;
  }
  return undefined;
}

function resolveStatusRowLayout(width: number): StatusRowLayout {
  const stackStatusRow = width < STATUS_ROW_SIDE_BY_SIDE_MIN_WIDTH;
  const narrowStatusRow =
    width >= STATUS_ROW_SIDE_BY_SIDE_MIN_WIDTH &&
    width <= STATUS_ROW_NARROW_MAX_WIDTH;
  const matchStatusRowHeights = !stackStatusRow;
  // Hug content on every side-by-side status|price row (incl. medium 1100–1320).
  // Stretching + justifyContent:center was leaving oversized empty padding in both cards.
  const useWideDesktopCompactHeights = matchStatusRowHeights;

  return {
    stackStatusRow,
    narrowStatusRow,
    matchStatusRowHeights,
    spreadStatusActions: width < STATUS_ACTIONS_SPREAD_MAX_WIDTH,
    statusRowMinHeight: resolveStatusRowMinHeight(
      matchStatusRowHeights,
      useWideDesktopCompactHeights,
    ),
    useWideDesktopCompactHeights,
    reservationActionSize: resolveReservationActionSize(width),
  };
}

/** A short row under the painted units while the next page is still in flight. */
const NEXT_PAGE_SKELETON_KEYS = Array.from(
  { length: 6 },
  (_, index) => `listing-next-skeleton-${index}`,
);

function getRelativeDayBadge(
  isoDate: string,
  listing: ListingLabels,
): string | undefined {
  const [y, m, d] = isoDate.split("-").map(Number);
  if (!y || !m || !d) return undefined;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const target = new Date(y, m - 1, d);
  target.setHours(0, 0, 0, 0);
  const diffDays = Math.round(
    (target.getTime() - today.getTime()) / (24 * 60 * 60 * 1000),
  );
  if (diffDays === 0) return listing.today;
  if (diffDays === 1) return listing.tomorrow;
  return undefined;
}

function formatAppointmentSummaryParts(
  appointment: BookedAppointment,
): Readonly<{ dateDisplay: string; timeLabel: string }> {
  return {
    dateDisplay: appointment.dateDisplay,
    timeLabel: appointment.timeLabel,
  };
}

/** Web RTL horizontal ScrollView reports negative contentOffset.x — mirror ImageCarousel. */
function getStatusCarouselScrollX(contentOffsetX: number, isRTL: boolean): number {
  return isRTL ? -contentOffsetX : contentOffsetX;
}

function getStatusCarouselScrollOffset(
  index: number,
  cardWidth: number,
  isRTL: boolean,
): number {
  const offset = index * cardWidth;
  return isRTL ? -offset : offset;
}

/** Full slider range (3M+) — API max_price is nine 9s (999_999_999). */
function isFullPriceRange(maxPriceMillions: number): boolean {
  // Thumb float drift near the right end still counts as max.
  return maxPriceMillions >= PRICE_SLIDER_MAX_M - 0.05;
}

/** Slider millions → AED max_price for GET /v1/units. */
function resolveMaxPriceFilterAed(maxPriceMillions: number): number {
  if (isFullPriceRange(maxPriceMillions)) {
    return UNITS_NO_PRICE_LIMIT_AED;
  }
  return maxPriceMillions * 1_000_000;
}

type ListingEmptyKind = 'fetch_error' | 'filter' | 'no_properties';

function resolveListingEmptyKind(
  isError: boolean,
  hasMaxPriceFilter: boolean,
): ListingEmptyKind {
  if (isError) {
    return 'fetch_error';
  }
  if (hasMaxPriceFilter) {
    return 'filter';
  }
  return 'no_properties';
}

function getListingEmptyCopy(
  kind: ListingEmptyKind,
  listing: ListingLabels,
): {
  title: string;
  subtitle: string;
} {
  if (kind === 'fetch_error') {
    return {
      title: listing.emptyTitle,
      subtitle: listing.emptyFetchError,
    };
  }
  if (kind === 'filter') {
    return {
      title: listing.emptyTitle,
      subtitle: listing.emptyFilterNoMatch,
    };
  }
  return {
    title: listing.emptyTitle,
    subtitle: listing.emptyNoProperties,
  };
}

function resolvePrimaryProjectId(
  displayListings: ListingUnit[],
  projectIdFromProfile: string,
): string {
  const firstListingProjectId = displayListings[0]?.project_id;
  return firstListingProjectId || projectIdFromProfile;
}

function usePropertiesListScreenModel() {
  const c = useUIColors();
  const { t } = useTranslation();
  const { listing, UNIT_DETAILS: unitLabels } = usePropertyLabels();
  const { isRTL, currentLanguage } = useI18nContext();
  const contentfulLocale = currentLanguage === 'ar' ? 'ar' : 'en';
  const { breakpointWidth: width } = useWebViewport();
  const isFocused = useIsFocused();
  const appointmentFlow = useBookAppointmentFlow();
  const { resetDialogs } = appointmentFlow;
  const isAppointmentFlowActive = appointmentFlow.isFlowActive;



  useEffect(() => {
    if (!isFocused && !isAppointmentFlowActive) {
      resetDialogs();
    }
  }, [isFocused, isAppointmentFlowActive, resetDialogs]);

  const isMobile = width < TABLET_MIN_WIDTH;
  const isTablet = width >= TABLET_MIN_WIDTH && width < TABLET_MAX_WIDTH;
  const statusRowLayout = useMemo(() => resolveStatusRowLayout(width), [width]);

  const profileLoanAmount = useAppStore((s) => s.auth.user?.loan_Amount);
  const storedLoanAmount = useAppStore(
    (s) => s.adhaLoanDetails?.sanctionedLoanAmount,
  );
  // Display-only on the price slider (loan label / eligibility). Not used for
  // slider default or GET /v1/units max_price.
  const iskanLoanAed = useMemo(
    () => resolveIskanLoanAmountAed(profileLoanAmount, storedLoanAmount),
    [profileLoanAmount, storedLoanAmount],
  );

  const [maxPrice, setMaxPrice] = React.useState(() =>
    defaultSliderMillionsForBedrooms(
      useUnitListingFilterStore.getState().appliedBedrooms,
      PRICE_SLIDER_MAX_M,
    ),
  );
  const [debouncedMaxPrice, setDebouncedMaxPrice] = React.useState(maxPrice);

  const handleMaxPriceChange = React.useCallback((value: number) => {
    setMaxPrice(value);
    setDebouncedMaxPrice(value);
  }, []);

  const filterApplyInitializedRef = React.useRef(false);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedMaxPrice(maxPrice);
    }, 500);
    return () => clearTimeout(handler);
  }, [maxPrice]);

  useEffect(() => {
    if (!filterApplyInitializedRef.current) {
      filterApplyInitializedRef.current = true;
      return;
    }
    trackCatalogEvent({
      screen: ALMETLAA_SCREEN.CATALOGUE,
      actionName: ALMETLAA_ACTION.CATALOGUE.FILTER_APPLY_CLICK,
      eventType: 'action',
      properties: { p_sectionName: String(debouncedMaxPrice) },
    });
  }, [debouncedMaxPrice]);

  const projectNameFromProfile = useAppStore(
    (s) =>
      s.adhaProjectDetails?.project_name ||
      s.adhaLoanDetails?.projectName ||
      '',
  );
  const projectIdFromProfile = useAppStore(
    (s) =>
      s.adhaProjectDetails?.project_id ||
      s.adhaLoanDetails?.projectId ||
      '',
  );
  const handoverFromProfile = useAppStore(
    (s) => s.adhaProjectDetails?.handover_date || '',
  );
  const projectLocationFromProfile = useAppStore(
    (s) => s.adhaProjectDetails?.project_location || '',
  );
  const userDisplayName = useAppStore(
    (s) => s.auth.user?.full_name || s.auth.user?.name || '',
  );
  const pendingUnitReservation = useAppStore(
    (s) => s.pendingUnitReservation,
  );
  const [cancelReservationVisible, setCancelReservationVisible] =
    React.useState(false);
  const [reservationCanceledVisible, setReservationCanceledVisible] =
    React.useState(false);
  const [cancelErrorMessage, setCancelErrorMessage] = React.useState<
    string | null
  >(null);
  const [isCancelReservationPending, setIsCancelReservationPending] =
    React.useState(false);
  const activeBookedUnitId = useAppStore((s) => s.activeBookedUnitId);
  // Only the reserved unit: subscribing to the whole list re-renders this screen
  // every time a page of units is copied into the store.
  const reservedUnitFromStore = useAppStore((s) => {
    const reservedId = s.activeBookedUnitId || s.pendingUnitReservation?.unitId;
    return reservedId
      ? s.availableUnits.find((unit) => unit.unit_id === reservedId)
      : undefined;
  });
  const customerId = useAppStore((s) => s.auth.user?.customer_id);
  const dynamicGreetingPrefix = resolveGreetingPrefix(t);
  const greeting = formatNamedGreeting(dynamicGreetingPrefix, userDisplayName);

  // Exclusive 3BR → 1.7M; any other bedroom selection → 3M+ sentinel.
  // User can still drag the slider after that default.
  const maxPriceFilterAed = resolveMaxPriceFilterAed(debouncedMaxPrice);

  const {
    data,
    isLoading: isLoadingUnits,
    isFetchingFirstPage,
    isFetchingNextPage,
    canLoadMore,
    hasMoreUnits,
    loadMore,
    isError,
  } = useAvailableUnits(undefined, maxPriceFilterAed);

  useCatalogDynatraceView(
    ALMETLAA_SCREEN.CATALOGUE,
    ALMETLAA_ACTION.CATALOGUE.HOME_VIEW,
    !isFetchingFirstPage && !isError,
  );

  // Booked unit is never in GET /v1/units — pin seed when unfiltered or within max_price.
  const displayListings = useMemo(() => {
    const queryUnits = (data?.units || []) as ListingUnit[];
    const reservedId =
      activeBookedUnitId || pendingUnitReservation?.unitId || null;
    if (!reservedId) {
      return queryUnits;
    }

    const seed =
      (pendingUnitReservation?.unitId === reservedId
        ? pendingUnitReservation.unit
        : undefined) ||
      (reservedUnitFromStore?.unit_id === reservedId ? reservedUnitFromStore : undefined);
    if (!seed) {
      return queryUnits;
    }

    if (maxPriceFilterAed != null) {
      const unitPrice = Number(seed.total_price);
      if (!Number.isFinite(unitPrice) || unitPrice > maxPriceFilterAed) {
        return queryUnits;
      }
    }

    const rest = queryUnits.filter((unit) => unit.unit_id !== reservedId);
    return [seed as ListingUnit, ...rest];
  }, [
    activeBookedUnitId,
    reservedUnitFromStore,
    data?.units,
    maxPriceFilterAed,
    pendingUnitReservation,
  ]);

  const appliedBedrooms = useUnitListingFilterStore((state) => state.appliedBedrooms);
  const setAppliedBedrooms = useUnitListingFilterStore((state) => state.setAppliedBedrooms);
  const filteredListings = useMemo(
    () => filterUnitsByBedrooms(displayListings, appliedBedrooms),
    [displayListings, appliedBedrooms],
  );
  const handleApplyBedrooms = React.useCallback(
    (bedrooms: readonly BedroomFilterOption[]) => {
      const defaultSliderM = defaultSliderMillionsForBedrooms(
        bedrooms,
        PRICE_SLIDER_MAX_M,
      );
      setAppliedBedrooms(bedrooms);
      setMaxPrice(defaultSliderM);
      setDebouncedMaxPrice(defaultSliderM);
      trackCatalogEvent({
        screen: ALMETLAA_SCREEN.CATALOGUE,
        actionName: ALMETLAA_ACTION.CATALOGUE.FILTER_APPLY_CLICK,
        eventType: 'action',
        properties: { p_sectionName: bedroomSelectionKey(bedrooms) },
      });
    },
    [setAppliedBedrooms],
  );

  const { bookingQuery, profileQuery } = useAdhaReservationStatus(
    customerId,
    pendingUnitReservation,
    projectNameFromProfile,
    projectLocationFromProfile,
  );
  const primaryProjectId = resolvePrimaryProjectId(
    displayListings,
    projectIdFromProfile,
  );

  const contentfulImagesQuery = useQuery({
    queryKey: ['projectImages', primaryProjectId, contentfulLocale],
    queryFn: async () => {
      if (!primaryProjectId) {
        return { images: [] };
      }
      const assets = await fetchPropertyAssets(primaryProjectId, {
        maxWidth: 1200,
        locale: contentfulLocale,
      });
      logger.info('PropertiesListScreen', 'propertyImages unitImages field', {
        projectId: primaryProjectId,
        propertyImageCount: assets.images.length,
        unitImageCount: assets.unitImages?.length ?? 0,
        unitImageUrls: (assets.unitImages ?? []).map((img) => img.url),
      });
      return assets;
    },
    enabled: !!primaryProjectId,
  });
  const contentfulAssets = contentfulImagesQuery.data ?? { images: [] };
  // Without a project id the query is disabled (and stays pending), so only wait
  // while the listing that may supply the id is still loading.
  const contentfulImagesLoading = primaryProjectId
    ? contentfulImagesQuery.isPending
    : isFetchingFirstPage || (displayListings.length === 0 && isLoadingUnits);
  /**
   * Galleries are chosen only after Contentful has succeeded, then again if
   * `unit_model` arrives on a later listing response. A listing that finishes
   * first stays on the placeholder instead of a fallback set.
   */
  const unitImageLookup = contentfulImagesQuery.isSuccess
    ? (contentfulAssets.unitImagesByUnitModel ?? EMPTY_UNIT_IMAGE_LOOKUP)
    : null;

  const contentfulProjectName = contentfulAssets.name?.trim() || undefined;
  const contentfulDescription =
    contentfulAssets.description?.trim() || undefined;
  const exploreVrBanner = contentfulAssets.exploreVrBanner ?? null;
  const masterPlanBanner = contentfulAssets.masterPlanBanner ?? null;

  const appointment = appointmentFlow.appointment ?? null;
  const hasAppointment = appointment != null;
  const isUpcomingLoading = appointmentFlow.isUpcomingLoading;
  const isBookingLoading =
    Boolean(customerId) &&
    (bookingQuery.isPending || bookingQuery.isFetching);
  const reservedListingCard = useMemo(() => {
    if (!customerId || isBookingLoading || !bookingQuery.isFetched) {
      return null;
    }
    if (bookingQuery.isError) {
      return null;
    }
    return resolveReservedListingCardFromBookingResponse(
      bookingQuery.data,
      displayListings,
      t,
      projectNameFromProfile,
      projectLocationFromProfile,
      contentfulProjectName,
      profileQuery.data,
    );
  }, [
    bookingQuery.data,
    bookingQuery.isError,
    bookingQuery.isFetched,
    customerId,
    displayListings,
    isBookingLoading,
    profileQuery.data,
    projectLocationFromProfile,
    projectNameFromProfile,
    contentfulProjectName,
    t,
  ]);
  const reservedUnitForActions = useMemo((): Pick<
    ReservedUnit,
    'propertyId' | 'parentId'
  > | null => {
    if (!reservedListingCard?.unitId) {
      return null;
    }
    const active = parseActiveBookingFromApi(bookingQuery.data);
    const unit: { propertyId: string; parentId?: string } = {
      propertyId: reservedListingCard.unitId,
    };
    if (active?.parentId) {
      unit.parentId = active.parentId;
    }
    return unit;
  }, [bookingQuery.data, reservedListingCard]);
  const { cancelReservation } = useReservationActions(reservedUnitForActions);
  const showStatusRow =
    isBookingLoading ||
    Boolean(reservedListingCard) ||
    isUpcomingLoading ||
    hasAppointment;
  // Book is an appointment action — never on the reservation card; FAB when no appointment yet.
  const isAppointmentBookingEnabled =
    isAdhaProfileActionEnabled(profileQuery.data?.isAppointmentBookingEnabled) &&
    !isAppointmentBookingWindowClosed(
      profileQuery.data?.appointmentBookingStartDate,
      profileQuery.data?.appointmentBookingEndDate,
    );
  const showBookFab =
    isAppointmentBookingEnabled &&
    appointmentFlow.isUpcomingResolved &&
    !hasAppointment;
  const relativeBadge = useMemo(
    () =>
      appointment
        ? getRelativeDayBadge(appointment.dateId, listing)
        : undefined,
    [appointment, listing],
  );

  const handleListingPress = React.useCallback((unitId: string) => {
    trackCatalogEvent({
      screen: ALMETLAA_SCREEN.CATALOGUE,
      actionName: ALMETLAA_ACTION.CATALOGUE.UNIT_CARD_CLICK,
      eventType: 'action',
      properties: { unitId },
    });
    // Navigate immediately; details route owns shimmer while content mounts.
    router.push(
      `/properties-web/details?unitId=${encodeURIComponent(unitId)}`,
    );
  }, []);

  const handleViewReservation = React.useCallback(() => {
    const unitId = reservedListingCard?.unitId;
    if (!reservedListingCard || !unitId) {
      return;
    }
    trackAdhaReservationCardClick(unitId);
    if (!getUnitDetailsLayout(width).isCompact) {
      trackReservationSummaryOpen(unitId);
    }
    router.push(
      `/properties-web/details?unitId=${encodeURIComponent(unitId)}&viewReservation=1`,
    );
  }, [reservedListingCard, width]);

  const handleCancelReservationPress = React.useCallback(() => {
    trackCatalogEvent({
      screen: ALMETLAA_SCREEN.RESERVE,
      actionName: ALMETLAA_ACTION.RESERVE.CANCEL_DIALOG_OPEN,
      eventType: 'action',
    });
    setCancelReservationVisible(true);
  }, []);

  const loadMoreListingsWithTracking = React.useCallback(() => {
    trackCatalogEvent({
      screen: ALMETLAA_SCREEN.CATALOGUE,
      actionName: ALMETLAA_ACTION.CATALOGUE.PAGE_CHANGE,
      eventType: 'action',
    });
    loadMore();
  }, [loadMore]);

  const handleCloseCancelReservation = React.useCallback(() => {
    if (isCancelReservationPending) {
      return;
    }
    setCancelReservationVisible(false);
  }, [isCancelReservationPending]);

  const handleConfirmCancelReservation = React.useCallback(async () => {
    if (isCancelReservationPending) {
      return;
    }
    setIsCancelReservationPending(true);
    const { success, message } = await cancelReservation();
    setIsCancelReservationPending(false);
    setCancelReservationVisible(false);
    if (success) {
      setReservationCanceledVisible(true);
      return;
    }
    setCancelErrorMessage(message || unitLabels.TOAST_CANCEL_FAILED);
  }, [
    cancelReservation,
    isCancelReservationPending,
    unitLabels.TOAST_CANCEL_FAILED,
  ]);

  const handleCloseReservationCanceled = React.useCallback(() => {
    setReservationCanceledVisible(false);
  }, []);

  const canCancelReservedListing =
    reservedListingCard != null &&
    RESERVATION_STATUS_CONFIG[reservedListingCard.mappedStatus]
      .canCancelReservation;

  return {
    c,
    t,
    isRTL,
    isFocused,
    appointmentFlow,
    isMobile,
    isTablet,
    statusRowLayout,
    maxPrice,
    setMaxPrice: handleMaxPriceChange,
    iskanLoanAed,
    projectNameFromProfile,
    projectIdFromProfile,
    projectLocationFromProfile,
    handoverFromProfile,
    greeting,
    displayListings: filteredListings,
    allListings: displayListings,
    appliedBedrooms,
    applyBedrooms: handleApplyBedrooms,
    listingFilterKey: bedroomSelectionKey(appliedBedrooms),
    contentfulImages: contentfulAssets.images,
    contentfulProjectName,
    contentfulDescription,
    contentfulBrochureUrls: resolveBrochureUrlsFromAssets(contentfulAssets),
    exploreVrBanner,
    masterPlanBanner,
    unitImageLookup,
    contentfulImagesLoading,
    showSkeleton:
      maxPrice !== debouncedMaxPrice ||
      isFetchingFirstPage ||
      (displayListings.length === 0 && isLoadingUnits),
    isLoadingMoreListings: isFetchingNextPage,
    canLoadMoreListings: canLoadMore,
    hasMoreUnits,
    loadMoreListings: loadMoreListingsWithTracking,
    isUnitsError: isError,
    hasMaxPriceFilter: maxPriceFilterAed != null,
    appointment,
    isUpcomingLoading,
    showStatusRow,
    reservedListingCard,
    isBookingLoading,
    showBookFab,
    relativeBadge,
    handleListingPress,
    handleViewReservation,
    handleCancelReservationPress,
    canCancelReservedListing,
    cancelReservationVisible,
    reservationCanceledVisible,
    cancelErrorMessage,
    isCancelReservationPending,
    setCancelErrorMessage,
    handleCloseCancelReservation,
    handleConfirmCancelReservation,
    handleCloseReservationCanceled,
    breakpointWidth: width,
  };
}

function AdhaPropertiesListContent() {
  const { listing, UNIT_DETAILS: unitLabels } = usePropertyLabels();
  const {
    c,
    t,
    isRTL,
    isFocused,
    appointmentFlow,
    isMobile,
    isTablet,
    statusRowLayout,
    maxPrice,
    setMaxPrice,
    iskanLoanAed,
    projectNameFromProfile,
    projectIdFromProfile,
    projectLocationFromProfile,
    handoverFromProfile,
    greeting,
    displayListings,
    allListings,
    appliedBedrooms,
    applyBedrooms,
    listingFilterKey,
    contentfulImages,
    contentfulProjectName,
    contentfulDescription,
    contentfulBrochureUrls,
    exploreVrBanner,
    masterPlanBanner,
    unitImageLookup,
    contentfulImagesLoading,
    showSkeleton,
    isLoadingMoreListings,
    canLoadMoreListings,
    hasMoreUnits,
    loadMoreListings,
    isUnitsError,
    hasMaxPriceFilter,
    appointment,
    isUpcomingLoading,
    showStatusRow,
    reservedListingCard,
    isBookingLoading,
    showBookFab,
    relativeBadge,
    handleListingPress,
    handleViewReservation,
    handleCancelReservationPress,
    canCancelReservedListing,
    cancelReservationVisible,
    reservationCanceledVisible,
    cancelErrorMessage,
    isCancelReservationPending,
    setCancelErrorMessage,
    handleCloseCancelReservation,
    handleConfirmCancelReservation,
    handleCloseReservationCanceled,
    breakpointWidth: width,
  } = usePropertiesListScreenModel();

  const projectDetails = useAppStore((state) => state.adhaProjectDetails);
  const [downloadingDocumentId, setDownloadingDocumentId] = useState<
    string | null
  >(null);

  const listingLayout = useMemo(() => getAdhaListingLayout(width), [width]);
  const [compactTab, setCompactTab] = useState<AdhaListingTab>('project');
  const [unitsGridWidth, setUnitsGridWidth] = useState(0);

  /**
   * Measure the grid (same width as the price filter) and size columns in px
   * so card edges line up with tabs/filter — % widths + negative margins slip on web.
   */
  const unitsFallbackWidth = Math.max(
    0,
    width - ADHA_LISTING_COMPACT_PADDING_X * 2,
  );
  const unitsLayoutWidth =
    unitsGridWidth > 0 ? unitsGridWidth : unitsFallbackWidth;
  const unitsColumnCount = listingLayout.isCompact
    ? getAdhaUnitsColumnCount(unitsLayoutWidth)
    : 1;
  const unitsColWidthPx = getAdhaUnitsColumnWidthPx(
    unitsLayoutWidth,
    unitsColumnCount,
    ADHA_UNITS_GRID_GAP,
  );
  const unitsColWidth: DimensionValue =
    unitsColWidthPx > 0 ? unitsColWidthPx : '100%';

  const viewportStoreRef = React.useRef<ReturnType<typeof createListingViewportStore> | null>(null);
  viewportStoreRef.current ??= createListingViewportStore();
  const viewportStore = viewportStoreRef.current;

  const listPaginationProps = useScrollPagination({
    onLoadMore: loadMoreListings,
    enabled: canLoadMoreListings && !showSkeleton,
  });
  const unitsScrollPagination = React.useMemo(
    () => ({
      ...listPaginationProps,
      onScroll: (event: Parameters<typeof listPaginationProps.onScroll>[0]) => {
        listPaginationProps.onScroll(event);
        viewportStore.update(
          event.nativeEvent.contentOffset.y,
          event.nativeEvent.layoutMeasurement.height,
        );
      },
      onLayout: (event: Parameters<typeof listPaginationProps.onLayout>[0]) => {
        listPaginationProps.onLayout(event);
        viewportStore.update(
          viewportStore.getRawScrollY(),
          event.nativeEvent.layout.height,
        );
      },
    }),
    [listPaginationProps, viewportStore],
  );

  const projectDisplayName =
    contentfulProjectName?.trim() || projectNameFromProfile.trim() || '';
  useWebDocumentTitle(projectDisplayName);
  const projectImages = useMemo(
    () =>
      contentfulImages
        .map((img) => ({ uri: img.url?.trim() ?? '' }))
        .filter((img) => Boolean(img.uri)),
    [contentfulImages],
  );

  const projectTypeTags = useMemo(() => {
    const seen = new Map<string, string>();
    for (const unit of allListings) {
      const label = String(unit.unit_type ?? '').trim();
      if (!label || seen.has(label)) {
        continue;
      }
      seen.set(label, label);
    }
    return [...seen.entries()].map(([id, label]) => ({ id, label }));
  }, [allListings]);

  /**
   * Count of the units currently shown (price + bedroom filters).
   * Stays a shimmer until paging finishes so a partial page is not shown as the total.
   */
  const projectUnitsCount = useMemo(() => {
    if (showSkeleton || hasMoreUnits) {
      return undefined;
    }
    return displayListings.length;
  }, [hasMoreUnits, displayListings.length, showSkeleton]);

  /** Available Units stays a shimmer until the API reports hasNext false. */
  const unitsCountLoading = showSkeleton || hasMoreUnits;

  /** Guest project-detail model (same shape as `mapProjectToDetails`). */
  const guestProjectDetails = useMemo(
    (): GuestUnitDetailsModel =>
      buildAdhaGuestProjectDetails({
        projectDetails,
        projectDisplayName,
        projectLocation: projectLocationFromProfile,
        contentfulDescription,
        contentfulImages,
        contentfulBrochureUrls,
        projectTypeTags,
        brochureLabel: unitLabels.BROCHURE,
        constructionCompletedLabel: unitLabels.CONSTRUCTION_COMPLETED,
      }),
    [
      contentfulBrochureUrls,
      contentfulDescription,
      contentfulImages,
      projectDetails,
      projectDisplayName,
      projectLocationFromProfile,
      projectTypeTags,
      unitLabels.BROCHURE,
      unitLabels.CONSTRUCTION_COMPLETED,
    ],
  );

  const handleProjectDocumentPress = React.useCallback(
    (documentId: string) => {
      if (downloadingDocumentId) {
        return;
      }
      const doc = guestProjectDetails.documents?.find(
        (item) => item.id === documentId,
      );
      if (!doc) {
        return;
      }
      if (doc.action === 'view') {
        const link = doc.url?.trim();
        if (!link) {
          return;
        }
        const href = /^https?:\/\//i.test(link) ? link : `https://${link}`;
        if (Platform.OS === 'web' && typeof window !== 'undefined') {
          window.open(href, '_blank', 'noopener,noreferrer');
          return;
        }
        Linking.openURL(href).catch(() => {
          // Non-critical external navigation.
        });
        return;
      }
      const urls = resolveDocumentDownloadUrls(doc);
      if (urls.length === 0 || !guestProjectDetails.title) {
        return;
      }
      setDownloadingDocumentId(documentId);
      openGuestDocument(urls, guestProjectDetails.title)
        .catch((error) => {
          logger.error('PropertiesListScreen', 'Failed to download document', {
            error,
          });
        })
        .finally(() => {
          setDownloadingDocumentId(null);
        });
    },
    [downloadingDocumentId, guestProjectDetails],
  );

  const handleProjectMapPress = React.useCallback(() => {
    const { latitude, longitude } = guestProjectDetails;
    if (latitude == null || longitude == null) {
      return;
    }
    const mapUrl = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
      `${latitude},${longitude}`,
    )}`;
    Linking.openURL(mapUrl).catch(() => {
      // Non-critical external navigation.
    });
  }, [guestProjectDetails]);

  const primaryProjectId = useMemo(
    () => resolvePrimaryProjectId(allListings, projectIdFromProfile),
    [allListings, projectIdFromProfile],
  );

  const unitsFilterControl = (
    <UnitListingInlineFilters
      units={allListings}
      appliedBedrooms={appliedBedrooms}
      onApplyBedrooms={applyBedrooms}
      onUnitPress={handleListingPress}
      projectName={projectDisplayName}
    />
  );

  const projectNameForAnalytics =
    projectDisplayName || projectNameFromProfile;

  const handleMasterPlanBannerPress = React.useCallback(() => {
    trackProjectPromoBannerClick({
      projectId: primaryProjectId,
      projectName: projectNameForAnalytics,
      kind: 'master_plan',
    });
  }, [primaryProjectId, projectNameForAnalytics]);

  const handleExploreVrBannerPress = React.useCallback(() => {
    trackProjectPromoBannerClick({
      projectId: primaryProjectId,
      projectName: projectNameForAnalytics,
      kind: 'explore_vr',
    });
  }, [primaryProjectId, projectNameForAnalytics]);

  const isAnyDialogOpen =
    cancelReservationVisible ||
    reservationCanceledVisible ||
    cancelErrorMessage != null ||
    appointmentFlow.isFlowActive;

  /**
   * Desktop split: cards live in the left flex pane (same width as carousel /
   * project column). Force stacked full-width so the 698px desktop cap does not
   * leave empty space beside the cards. Price filter is in the sibling column —
   * keep matchStatusRowHeights so both panes stretch to the same height.
   */
  const desktopStatusLayout = useMemo(
    (): StatusRowLayout => ({
      ...statusRowLayout,
      stackStatusRow: true,
      narrowStatusRow: false,
      matchStatusRowHeights: true,
      statusRowMinHeight: resolveStatusRowMinHeight(
        true,
        statusRowLayout.useWideDesktopCompactHeights,
      ),
    }),
    [statusRowLayout],
  );

  /**
   * Tab (compact) sticky cards: always full-width stack so equal-split flex:1
   * never collapses the card under the hero (1100–1145).
   */
  const compactStatusLayout = useMemo(
    (): StatusRowLayout => ({
      ...statusRowLayout,
      stackStatusRow: true,
      narrowStatusRow: false,
      matchStatusRowHeights: false,
      useWideDesktopCompactHeights: false,
      statusRowMinHeight: undefined,
    }),
    [statusRowLayout],
  );

  return (
    <ListingViewportContext.Provider value={viewportStore}>
    <AdhaListingScreenFrame
      listing={listing}
      listingLayout={listingLayout}
      isMobile={isMobile}
      isTablet={isTablet}
      isRTL={isRTL}
      isFocused={isFocused}
      greeting={greeting}
      showStatusRow={showStatusRow}
      showBookFab={showBookFab}
      isUpcomingLoading={isUpcomingLoading}
      reservedListingCard={reservedListingCard}
      isBookingLoading={isBookingLoading}
      appointment={appointment}
      relativeBadge={relativeBadge}
      statusRowLayout={statusRowLayout}
      desktopStatusLayout={desktopStatusLayout}
      compactStatusLayout={compactStatusLayout}
      colors={c}
      maxPrice={maxPrice}
      iskanLoanAed={iskanLoanAed}
      setMaxPrice={setMaxPrice}
      appointmentFlow={appointmentFlow}
      handleViewReservation={handleViewReservation}
      handleCancelReservationPress={handleCancelReservationPress}
      canCancelReservedListing={canCancelReservedListing}
      isAnyDialogOpen={isAnyDialogOpen}
      guestProjectDetails={guestProjectDetails}
      projectImages={projectImages}
      contentfulImagesLoading={contentfulImagesLoading}
      exploreVrBanner={exploreVrBanner}
      masterPlanBanner={masterPlanBanner}
      handleProjectMapPress={handleProjectMapPress}
      handleProjectDocumentPress={handleProjectDocumentPress}
      handleMasterPlanBannerPress={handleMasterPlanBannerPress}
      handleExploreVrBannerPress={handleExploreVrBannerPress}
      downloadingDocumentId={downloadingDocumentId}
      showSkeleton={showSkeleton}
      displayListings={displayListings}
      unitsColWidth={unitsColWidth}
      unitsColumnCount={unitsColumnCount}
      projectNameFromProfile={projectNameFromProfile}
      projectLocationFromProfile={projectLocationFromProfile}
      handoverFromProfile={handoverFromProfile}
      unitImageLookup={unitImageLookup}
      contentfulProjectName={contentfulProjectName}
      hasMaxPriceFilter={hasMaxPriceFilter}
      isUnitsError={isUnitsError}
      isLoadingMoreListings={isLoadingMoreListings}
      handleListingPress={handleListingPress}
      listPaginationProps={unitsScrollPagination}
      compactTab={compactTab}
      setCompactTab={setCompactTab}
      setUnitsGridWidth={setUnitsGridWidth}
      cancelReservationVisible={cancelReservationVisible}
      reservationCanceledVisible={reservationCanceledVisible}
      cancelErrorMessage={cancelErrorMessage}
      isCancelReservationPending={isCancelReservationPending}
      setCancelErrorMessage={setCancelErrorMessage}
      handleCloseCancelReservation={handleCloseCancelReservation}
      handleConfirmCancelReservation={handleConfirmCancelReservation}
      handleCloseReservationCanceled={handleCloseReservationCanceled}
      t={t}
      projectUnitsCount={projectUnitsCount}
      unitsCountLoading={unitsCountLoading}
      unitsFilterControl={unitsFilterControl}
      listingFilterKey={listingFilterKey}
    />
    </ListingViewportContext.Provider>
  );

}

function buildAdhaListingHeadingTypography(params: Readonly<{
  text: string;
  isMobile: boolean;
  isRTL: boolean;
}>) {
  return (
    <Typography
      color="primary"
      style={{
        fontSize: params.isMobile ? 22 : 28,
        fontWeight: '500',
        lineHeight: params.isMobile ? 22 : 28,
        fontFamily: 'Matter-Medium',
        textAlign: params.isRTL ? 'right' : 'left',
      }}
    >
      {params.text}
    </Typography>
  );
}

function AdhaDesktopTitleRow(params: Readonly<{
  isCompact: boolean;
  greetingTypography: React.ReactNode;
}>) {
  const { breakpointWidth } = useWebViewport();
  const pagePadX = getWebPageContentPadding(breakpointWidth < BREAKPOINTS.SHELL);

  if (params.isCompact) {
    return null;
  }
  // Greeting only (Explore Units title removed). Empty right pane keeps the
  // 729:442 column align with the status/filter row + split below (no extra
  // scroll gutter — that skewed card width vs the carousel).
  return (
    <View style={[styles.alignRow, { paddingHorizontal: pagePadX }]}>
      <View style={styles.alignLeft}>{params.greetingTypography}</View>
      <View style={styles.alignRight} />
    </View>
  );
}

/** Associated Units row — white card; shimmer while loading. */
function AdhaUnitsCountBelowFilter(params: Readonly<{
  unitsCount: number | undefined;
  label: string;
  isLoading?: boolean;
}>) {
  return <AdhaUnitsCountCard {...params} />;
}

function AdhaUnitsCountCard(params: Readonly<{
  unitsCount: number | undefined;
  label: string;
  isLoading?: boolean;
}>) {
  const colors = useUIColors();
  const { breakpointWidth } = useWebViewport();
  const isPhone = breakpointWidth < BREAKPOINTS.PHONE;
  const labelVariant = isPhone ? 'headline' : 'title2';
  const skeletonHeight = isPhone ? 18 : 22;

  if (params.isLoading) {
    return (
      <View
        style={[
          styles.unitsCountBelowFilter,
          { backgroundColor: colors.backgroundLevel1 },
        ]}
      >
        <Skeleton width="42%" height={skeletonHeight} borderRadius={4} />
        <Skeleton width={48} height={skeletonHeight} borderRadius={4} />
      </View>
    );
  }
  if (params.unitsCount == null) {
    return null;
  }
  return (
    <View
      style={[
        styles.unitsCountBelowFilter,
        { backgroundColor: colors.backgroundLevel1 },
      ]}
    >
      <Typography
        variant={labelVariant}
        color="primary"
        style={styles.unitsCountLabel}
      >
        {params.label}
      </Typography>
      <View style={styles.unitsCountValueRow}>
        <LayersIcon size={isPhone ? 16 : 18} color={colors.fontPrimary} />
        <Typography
          variant={labelVariant}
          color="primary"
          style={styles.unitsCountValue}
        >
          {String(params.unitsCount)}
        </Typography>
      </View>
    </View>
  );
}

function AdhaDesktopStatusFilterRow(params: Readonly<{
  isCompact: boolean;
  showStatusRow: boolean;
  greeting: string;
  isUpcomingLoading: boolean;
  reservedListingCard: ReservedListingCard | null;
  isBookingLoading: boolean;
  appointment: BookedAppointment | null | undefined;
  relativeBadge: string | undefined;
  isMobile: boolean;
  isTablet: boolean;
  desktopStatusLayout: StatusRowLayout;
  isRTL: boolean;
  colors: ReturnType<typeof useUIColors>;
  maxPrice: number;
  iskanLoanAed: number;
  setMaxPrice: (value: number) => void;
  appointmentFlow: ReturnType<typeof useBookAppointmentFlow>;
  handleViewReservation: () => void;
  handleCancelReservationPress: () => void;
  canCancelReservedListing: boolean;
  isAnyDialogOpen: boolean;
  unitsFilterControl: React.ReactNode;
  onFiltersOverflowChange: (overflow: number) => void;
}>) {
  const { breakpointWidth } = useWebViewport();
  const pagePadX = getWebPageContentPadding(breakpointWidth < BREAKPOINTS.SHELL);

  // No status cards → price lives in sidebar next to the project carousel (case 3).
  if (params.isCompact || !params.showStatusRow) {
    return null;
  }

  /**
   * CSS grid: row1 = card | price (equal height = taller of the two).
   * row2 = dots under card only — pushes project/units down, does NOT change row1 height.
   */
  const priceFilterSlot = (
    <View style={styles.cardPriceMatchGridPriceInner}>
      <PriceRangeSlider
        value={params.maxPrice}
        loanAmountAed={params.iskanLoanAed}
        onValueChange={params.setMaxPrice}
        fullWidth
        fillAvailableHeight
        compactStatusRow={params.desktopStatusLayout.useWideDesktopCompactHeights}
        attachedBelow
      />
    </View>
  );
  // Continues the price card (same border, divider on top) so the two read as one card.
  const belowPriceSlot = (
    <View
      style={[
        styles.unitsFiltersCard,
        { backgroundColor: params.colors.backgroundLevel1, borderColor: params.colors.outlineInactive },
      ]}
    >
      <View style={[styles.unitsFiltersDivider, { backgroundColor: UI_PRIMITIVES.borderSlate }]} />
      {params.unitsFilterControl}
    </View>
  );

  return (
    <View style={[styles.cardPriceMatchGrid, { paddingHorizontal: pagePadX }]}>
      <ListingStatusCardView
        reservedListingCard={params.reservedListingCard}
        isBookingLoading={params.isBookingLoading}
        isUpcomingLoading={params.isUpcomingLoading}
        appointment={params.appointment}
        relativeBadge={params.relativeBadge}
        isMobile={params.isMobile}
        isTablet={params.isTablet}
        isRTL={params.isRTL}
        statusRowLayout={params.desktopStatusLayout}
        colors={params.colors}
        onCancel={() => {
          params.appointmentFlow.openCancel();
        }}
        onReschedule={() => {
          params.appointmentFlow.openReschedule();
        }}
        onViewReservation={params.handleViewReservation}
        onCancelReservation={params.handleCancelReservationPress}
        canCancelReservedListing={params.canCancelReservedListing}
        isAnyDialogOpen={params.isAnyDialogOpen}
        priceFilterSlot={priceFilterSlot}
        belowPriceSlot={belowPriceSlot}
        onBelowPriceOverflowChange={params.onFiltersOverflowChange}
      />
    </View>
  );
}

function buildAdhaCompactChrome(params: Readonly<{
  isCompact: boolean;
  showStatusRow: boolean;
  greetingTypography: React.ReactNode;
  greeting: string;
  isUpcomingLoading: boolean;
  reservedListingCard: ReservedListingCard | null;
  isBookingLoading: boolean;
  appointment: BookedAppointment | null | undefined;
  relativeBadge: string | undefined;
  isMobile: boolean;
  isTablet: boolean;
  compactStatusLayout: StatusRowLayout;
  isRTL: boolean;
  colors: ReturnType<typeof useUIColors>;
  maxPrice: number;
  iskanLoanAed: number;
  setMaxPrice: (value: number) => void;
  appointmentFlow: ReturnType<typeof useBookAppointmentFlow>;
  handleViewReservation: () => void;
  handleCancelReservationPress: () => void;
  canCancelReservedListing: boolean;
  isAnyDialogOpen: boolean;
  projectUnitsCount: number | undefined;
  unitsCountLabel: string;
  unitsCountLoading: boolean;
  unitsFilterControl: React.ReactNode;
}>) {
  if (!params.isCompact) {
    return {
      compactHeader: null as React.ReactNode,
      compactStatusSticky: null as React.ReactNode,
      compactPriceSticky: null as React.ReactNode,
    };
  }

  const compactHeader = params.greetingTypography;
  let compactStatusSticky: React.ReactNode = null;
  if (params.showStatusRow) {
    compactStatusSticky = (
      <PropertiesListHeader
        greeting={params.greeting}
        isUpcomingLoading={params.isUpcomingLoading}
        showStatusRow={params.showStatusRow}
        reservedListingCard={params.reservedListingCard}
        isBookingLoading={params.isBookingLoading}
        appointment={params.appointment}
        relativeBadge={params.relativeBadge}
        isMobile={params.isMobile}
        isTablet={params.isTablet}
        statusRowLayout={params.compactStatusLayout}
        isRTL={params.isRTL}
        colors={params.colors}
        maxPrice={params.maxPrice}
        iskanLoanAed={params.iskanLoanAed}
        onMaxPriceChange={params.setMaxPrice}
        onCancel={() => {
          params.appointmentFlow.openCancel();
        }}
        onReschedule={() => {
          params.appointmentFlow.openReschedule();
        }}
        onViewReservation={params.handleViewReservation}
        onCancelReservation={params.handleCancelReservationPress}
        canCancelReservedListing={params.canCancelReservedListing}
        isAnyDialogOpen={params.isAnyDialogOpen}
        hidePriceFilter
        hideGreeting
      />
    );
  }

  const compactPriceSticky = (
    <View style={styles.unitsPriceFilter}>
      <PriceRangeSlider
        value={params.maxPrice}
        loanAmountAed={params.iskanLoanAed}
        onValueChange={params.setMaxPrice}
        fullWidth
        footer={params.unitsFilterControl}
      />
      <AdhaUnitsCountBelowFilter
        unitsCount={params.projectUnitsCount}
        label={params.unitsCountLabel}
        isLoading={params.unitsCountLoading}
      />
    </View>
  );

  return { compactHeader, compactStatusSticky, compactPriceSticky };
}

function buildAdhaUnitsColumn(params: Readonly<{
  listingLayout: ReturnType<typeof getAdhaListingLayout>;
  showSkeleton: boolean;
  displayListings: ListingUnit[];
  unitsColWidth: DimensionValue;
  unitsColumnCount: number;
  projectNameFromProfile: string;
  projectLocationFromProfile: string;
  handoverFromProfile: string;
  unitImageLookup: Readonly<Record<string, readonly PropertyImage[]>> | null;
  contentfulImagesLoading: boolean;
  contentfulProjectName: string | undefined;
  hasMaxPriceFilter: boolean;
  isUnitsError: boolean;
  isLoadingMoreListings: boolean;
  handleListingPress: (unitId: string) => void;
  setUnitsGridWidth: React.Dispatch<React.SetStateAction<number>>;
  listingFilterKey: string;
}>) {
  return (
    <View style={styles.unitsColumn}>
      <View
        style={styles.grid}
        onLayout={(event) => {
          const nextWidth = event.nativeEvent.layout.width;
          params.setUnitsGridWidth((prev) => {
            if (Math.abs(prev - nextWidth) < 0.5) {
              return prev;
            }
            return nextWidth;
          });
        }}
      >
        <ListingsGridContent
          showSkeleton={params.showSkeleton}
          displayListings={params.displayListings}
          colWidth={params.unitsColWidth}
          singleColumn={
            params.listingLayout.isTablet && params.unitsColumnCount === 1
          }
          columnCount={params.unitsColumnCount}
          projectNameFromProfile={params.projectNameFromProfile}
          projectLocationFromProfile={params.projectLocationFromProfile}
          handoverFromProfile={params.handoverFromProfile}
          unitImageLookup={params.unitImageLookup}
          imagesLoading={params.contentfulImagesLoading}
          contentfulProjectName={params.contentfulProjectName ?? ''}
          hasMaxPriceFilter={params.hasMaxPriceFilter}
          isUnitsError={params.isUnitsError}
          isLoadingMore={params.isLoadingMoreListings}
          onListingPress={params.handleListingPress}
          listKey={params.listingFilterKey}
        />
      </View>
    </View>
  );
}

function AdhaListingScreenOverlays(params: Readonly<{
  showBookFab: boolean;
  isMobile: boolean;
  isFocused: boolean;
  appointmentFlow: ReturnType<typeof useBookAppointmentFlow>;
  listing: ListingLabels;
  cancelReservationVisible: boolean;
  reservationCanceledVisible: boolean;
  cancelErrorMessage: string | null;
  isCancelReservationPending: boolean;
  setCancelErrorMessage: (message: string | null) => void;
  handleCloseCancelReservation: () => void;
  handleConfirmCancelReservation: () => void;
  handleCloseReservationCanceled: () => void;
}>) {
  const showAppointmentDialogs =
    params.isFocused || params.appointmentFlow.isFlowActive;

  return (
    <>
      {params.showBookFab ? (
        <BookAppointmentFab
          docked={params.isMobile}
          onPress={params.appointmentFlow.openBook}
        />
      ) : null}

      {showAppointmentDialogs ? (
        <AppointmentFlowDialogs
          flow={params.appointmentFlow}
          rescheduleSubmitLabel={params.listing.reschedule}
        />
      ) : null}

      <CancelReservationDialog
        visible={params.cancelReservationVisible}
        onNo={params.handleCloseCancelReservation}
        onYes={params.handleConfirmCancelReservation}
        isConfirming={params.isCancelReservationPending}
      />
      <FullScreenLoader visible={params.isCancelReservationPending} />
      <ReservationCanceledDialog
        visible={params.reservationCanceledVisible}
        onDismiss={params.handleCloseReservationCanceled}
      />
      <OkDialog
        visible={params.cancelErrorMessage != null}
        message={params.cancelErrorMessage ?? ''}
        onOk={() => params.setCancelErrorMessage(null)}
      />
    </>
  );
}

function AdhaListingScreenFrame({
  listing,
  listingLayout,
  isMobile,
  isTablet,
  isRTL,
  isFocused,
  greeting,
  showStatusRow,
  showBookFab,
  isUpcomingLoading,
  reservedListingCard,
  isBookingLoading,
  appointment,
  relativeBadge,
  statusRowLayout,
  desktopStatusLayout,
  compactStatusLayout,
  colors: c,
  maxPrice,
  iskanLoanAed,
  setMaxPrice,
  appointmentFlow,
  handleViewReservation,
  handleCancelReservationPress,
  canCancelReservedListing,
  isAnyDialogOpen,
  guestProjectDetails,
  projectImages,
  contentfulImagesLoading,
  exploreVrBanner,
  masterPlanBanner,
  handleProjectMapPress,
  handleProjectDocumentPress,
  handleMasterPlanBannerPress,
  handleExploreVrBannerPress,
  downloadingDocumentId,
  showSkeleton,
  displayListings,
  unitsColWidth,
  unitsColumnCount,
  projectNameFromProfile,
  projectLocationFromProfile,
  handoverFromProfile,
  unitImageLookup,
  contentfulProjectName,
  hasMaxPriceFilter,
  isUnitsError,
  isLoadingMoreListings,
  handleListingPress,
  listPaginationProps,
  compactTab,
  setCompactTab,
  setUnitsGridWidth,
  cancelReservationVisible,
  reservationCanceledVisible,
  cancelErrorMessage,
  isCancelReservationPending,
  setCancelErrorMessage,
  handleCloseCancelReservation,
  handleConfirmCancelReservation,
  handleCloseReservationCanceled,
  t,
  projectUnitsCount,
  unitsCountLoading,
  unitsFilterControl,
  listingFilterKey,
}: Readonly<{
  listing: ListingLabels;
  listingLayout: ReturnType<typeof getAdhaListingLayout>;
  isMobile: boolean;
  isTablet: boolean;
  isRTL: boolean;
  isFocused: boolean;
  greeting: string;
  showStatusRow: boolean;
  showBookFab: boolean;
  isUpcomingLoading: boolean;
  reservedListingCard: ReservedListingCard | null;
  isBookingLoading: boolean;
  appointment: BookedAppointment | null | undefined;
  relativeBadge: string | undefined;
  statusRowLayout: StatusRowLayout;
  desktopStatusLayout: StatusRowLayout;
  compactStatusLayout: StatusRowLayout;
  colors: ReturnType<typeof useUIColors>;
  maxPrice: number;
  iskanLoanAed: number;
  setMaxPrice: (value: number) => void;
  appointmentFlow: ReturnType<typeof useBookAppointmentFlow>;
  handleViewReservation: () => void;
  handleCancelReservationPress: () => void;
  canCancelReservedListing: boolean;
  isAnyDialogOpen: boolean;
  guestProjectDetails: GuestUnitDetailsModel;
  projectImages: ReadonlyArray<{ uri: string }>;
  contentfulImagesLoading: boolean;
  exploreVrBanner: ExploreVrBannerData | null;
  masterPlanBanner: ExploreVrBannerData | null;
  handleProjectMapPress: () => void;
  handleProjectDocumentPress: (documentId: string) => void;
  handleMasterPlanBannerPress: () => void;
  handleExploreVrBannerPress: () => void;
  downloadingDocumentId: string | null;
  showSkeleton: boolean;
  displayListings: ListingUnit[];
  unitsColWidth: DimensionValue;
  unitsColumnCount: number;
  projectNameFromProfile: string;
  projectLocationFromProfile: string;
  handoverFromProfile: string;
  unitImageLookup: Readonly<Record<string, readonly PropertyImage[]>> | null;
  contentfulProjectName: string | undefined;
  hasMaxPriceFilter: boolean;
  isUnitsError: boolean;
  isLoadingMoreListings: boolean;
  handleListingPress: (unitId: string) => void;
  listPaginationProps: ReturnType<typeof useScrollPagination>;
  compactTab: AdhaListingTab;
  setCompactTab: React.Dispatch<React.SetStateAction<AdhaListingTab>>;
  setUnitsGridWidth: React.Dispatch<React.SetStateAction<number>>;
  cancelReservationVisible: boolean;
  reservationCanceledVisible: boolean;
  cancelErrorMessage: string | null;
  isCancelReservationPending: boolean;
  setCancelErrorMessage: (message: string | null) => void;
  handleCloseCancelReservation: () => void;
  handleConfirmCancelReservation: () => void;
  handleCloseReservationCanceled: () => void;
  t: PropertyTranslateFn;
  projectUnitsCount: number | undefined;
  unitsCountLoading: boolean;
  unitsFilterControl: React.ReactNode;
  listingFilterKey: string;
}>) {
  const greetingTypography = buildAdhaListingHeadingTypography({
    text: greeting,
    isMobile,
    isRTL,
  });
  const unitsCountLabel = listing.associatedUnits;
  // How far the pinned search/bedrooms card hangs below the desktop status row.
  const [filtersOverflow, setFiltersOverflow] = useState(0);

  const titleRow = (
    <AdhaDesktopTitleRow
      isCompact={listingLayout.isCompact}
      greetingTypography={greetingTypography}
    />
  );

  const statusFilterRow = (
    <AdhaDesktopStatusFilterRow
      isCompact={listingLayout.isCompact}
      showStatusRow={showStatusRow}
      greeting={greeting}
      isUpcomingLoading={isUpcomingLoading}
      reservedListingCard={reservedListingCard}
      isBookingLoading={isBookingLoading}
      appointment={appointment}
      relativeBadge={relativeBadge}
      isMobile={isMobile}
      isTablet={isTablet}
      desktopStatusLayout={desktopStatusLayout}
      isRTL={isRTL}
      colors={c}
      maxPrice={maxPrice}
      iskanLoanAed={iskanLoanAed}
      setMaxPrice={setMaxPrice}
      appointmentFlow={appointmentFlow}
      handleViewReservation={handleViewReservation}
      handleCancelReservationPress={handleCancelReservationPress}
      canCancelReservedListing={canCancelReservedListing}
      isAnyDialogOpen={isAnyDialogOpen}
      unitsFilterControl={unitsFilterControl}
      onFiltersOverflowChange={setFiltersOverflow}
    />
  );

  const { compactHeader, compactStatusSticky, compactPriceSticky } =
    buildAdhaCompactChrome({
      isCompact: listingLayout.isCompact,
      showStatusRow,
      greetingTypography,
      greeting,
      isUpcomingLoading,
      reservedListingCard,
      isBookingLoading,
      appointment,
      relativeBadge,
      isMobile,
      isTablet,
      compactStatusLayout,
      isRTL,
      colors: c,
      maxPrice,
      iskanLoanAed,
      setMaxPrice,
      appointmentFlow,
      handleViewReservation,
      handleCancelReservationPress,
      canCancelReservedListing,
      isAnyDialogOpen,
      projectUnitsCount,
      unitsCountLabel,
      unitsCountLoading,
      unitsFilterControl,
    });

  const projectColumn = (
    <View style={styles.projectColumn}>
      <AdhaListingProjectPanel
        layout={listingLayout}
        project={guestProjectDetails}
        images={projectImages}
        imagesLoading={contentfulImagesLoading}
        onMapPress={handleProjectMapPress}
        onDocumentPress={handleProjectDocumentPress}
        downloadingDocumentId={downloadingDocumentId}
        exploreVrBanner={exploreVrBanner}
        masterPlanBanner={masterPlanBanner}
        onMasterPlanBannerPress={handleMasterPlanBannerPress}
        onExploreVrBannerPress={handleExploreVrBannerPress}
      />
    </View>
  );

  // Figma:
  // - With status cards: price beside cards; Associated Units sticky ↔ carousel
  // - No status cards: price + Associated Units both sticky ↔ carousel
  let sidebarSticky: React.ReactNode = null;
  if (!listingLayout.isCompact) {
    if (showStatusRow) {
      // Reserve right-column space for the card pinned under the price card.
      const filtersSpacer =
        filtersOverflow > 0
          ? filtersOverflow - DESKTOP_ROOT_ROW_GAP + FILTERS_TO_COUNT_GAP
          : 0;
      sidebarSticky = (
        <View>
          <View style={{ height: Math.max(0, filtersSpacer) }} />
          <AdhaUnitsCountBelowFilter
            unitsCount={projectUnitsCount}
            label={unitsCountLabel}
            isLoading={unitsCountLoading}
          />
        </View>
      );
    } else {
      sidebarSticky = (
        <View style={styles.unitsPriceFilter}>
          <PriceRangeSlider
            value={maxPrice}
            loanAmountAed={iskanLoanAed}
            onValueChange={setMaxPrice}
            fullWidth
            footer={unitsFilterControl}
          />
          <AdhaUnitsCountBelowFilter
            unitsCount={projectUnitsCount}
            label={unitsCountLabel}
            isLoading={unitsCountLoading}
          />
        </View>
      );
    }
  }

  const unitsColumn = buildAdhaUnitsColumn({
    listingLayout,
    showSkeleton,
    displayListings,
    unitsColWidth,
    unitsColumnCount,
    projectNameFromProfile,
    projectLocationFromProfile,
    handoverFromProfile,
    unitImageLookup,
    contentfulImagesLoading,
    contentfulProjectName,
    hasMaxPriceFilter,
    isUnitsError,
    isLoadingMoreListings,
    handleListingPress,
    setUnitsGridWidth,
    listingFilterKey,
  });

  return (
    <WebAppLayout
      activeTab="home"
      headerTitle=""
      breadcrumbs={[]}
      showBackButton={false}
      hideSidebar={true}
    >
      <View
        style={[
          styles.pageRoot,
          { backgroundColor: c.backgroundLevel2 },
          listingLayout.isCompact && styles.pageRootCompact,
        ]}
      >
        <AdhaListingLayoutShell
          layout={listingLayout}
          compactTab={compactTab}
          onSelectCompactTab={(tab) => {
            startTransition(() => {
              setCompactTab(tab);
            });
          }}
          projectTabLabel={listing.projectDetailsTab}
          unitsTabLabel={listing.exploreUnitsTab}
          titleRow={titleRow}
          statusFilterRow={statusFilterRow}
          sidebarSticky={sidebarSticky}
          compactHeader={compactHeader}
          compactStatusSticky={compactStatusSticky}
          compactPriceSticky={compactPriceSticky}
          projectColumn={projectColumn}
          unitsColumn={unitsColumn}
          unitsScrollPagination={listPaginationProps}
        />

        <AdhaListingScreenOverlays
          showBookFab={showBookFab}
          isMobile={isMobile}
          isFocused={isFocused}
          appointmentFlow={appointmentFlow}
          listing={listing}
          cancelReservationVisible={cancelReservationVisible}
          reservationCanceledVisible={reservationCanceledVisible}
          cancelErrorMessage={cancelErrorMessage}
          isCancelReservationPending={isCancelReservationPending}
          setCancelErrorMessage={setCancelErrorMessage}
          handleCloseCancelReservation={handleCloseCancelReservation}
          handleConfirmCancelReservation={handleConfirmCancelReservation}
          handleCloseReservationCanceled={handleCloseReservationCanceled}
        />
      </View>
    </WebAppLayout>
  );
}

export function PropertiesListScreen() {
  return <AdhaPropertiesListContent />;
}

/**
 * Brochure only. Empty → no documents section.
 * Master plan is the banner above VR, not a row under Brochure.
 */
function buildAdhaProjectDocuments(params: Readonly<{
  brochureUrls: ReadonlyArray<string>;
  brochureLabel: string;
}>): GuestUnitDocument[] {
  const brochureUrls = params.brochureUrls.filter(Boolean);
  if (brochureUrls.length === 0) {
    return [];
  }
  return [{
    id: 'contentful-brochure',
    title: params.brochureLabel,
    url: brochureUrls[0] ?? '',
    urls: brochureUrls,
    action: 'download',
  }];
}

function resolveAdhaConstructionStatusBadge(
  rawCompletion: string | number | null | undefined,
  template: string,
): string | undefined {
  if (rawCompletion == null || rawCompletion === '') {
    return undefined;
  }
  const parsed = Number.parseFloat(String(rawCompletion));
  if (!Number.isFinite(parsed) || parsed <= 0) {
    return undefined;
  }
  const percent = Math.min(100, Math.max(0, parsed));
  return template.replace('{percent}', String(percent));
}

function buildAdhaGuestProjectDetails(params: Readonly<{
  projectDetails: UnitDetailsData | null | undefined;
  projectDisplayName: string;
  projectLocation: string;
  contentfulDescription: string | undefined;
  contentfulImages: ReadonlyArray<{ url?: string }>;
  contentfulBrochureUrls: ReadonlyArray<string>;
  projectTypeTags: GuestUnitDetailsModel['typeTags'];
  brochureLabel: string;
  constructionCompletedLabel: string;
}>): GuestUnitDetailsModel {
  const amenities = normalizeAmenitiesList(params.projectDetails?.amenities);
  const documents = buildAdhaProjectDocuments({
    brochureUrls: params.contentfulBrochureUrls,
    brochureLabel: params.brochureLabel,
  });

  const lat = params.projectDetails?.latitude;
  const lng = params.projectDetails?.longitude;
  const hasCoords =
    typeof lat === 'number' &&
    Number.isFinite(lat) &&
    typeof lng === 'number' &&
    Number.isFinite(lng);

  const statusBadge = resolveAdhaConstructionStatusBadge(
    params.projectDetails?.completion_percentage,
    params.constructionCompletedLabel,
  );

  const images = params.contentfulImages
    .map((img) => ({ uri: img.url?.trim() ?? '' }))
    .filter((img) => Boolean(img.uri));

  const base: GuestUnitDetailsModel = {
    id: params.projectDetails?.project_id?.trim() || 'adha-project',
    title: params.projectDisplayName,
    location: params.projectLocation.trim(),
    description: (params.contentfulDescription ?? '').trim(),
    images,
    typeTags: params.projectTypeTags,
    amenities,
    ...(documents.length > 0 ? { documents } : {}),
    ...(hasCoords ? { latitude: lat, longitude: lng } : {}),
    ...(statusBadge ? { statusBadge } : {}),
  };

  // Units count lives under the price filter on ADHA home — not in About.
  return base;
}

type ReservedListingCard = {
  mappedStatus: ReservationStatus;
  rawStatus: string | null;
  unitTitle: string;
  unitId: string;
};

function resolveReservedListingUnitTitle(
  matchedListing: ListingUnit | undefined,
  bookingListingUnit: AvailableUnit | null,
  active: { unitNumberHint?: string; unitId: string },
  fallbackProjectName: string | undefined,
  fallbackProjectLocation: string | undefined,
  contentfulProjectName?: string,
): string {
  if (matchedListing) {
    return listingUnitTitle(
      matchedListing,
      fallbackProjectName,
      fallbackProjectLocation,
      contentfulProjectName,
    );
  }
  if (bookingListingUnit) {
    return listingUnitTitle(
      bookingListingUnit as ListingUnit,
      fallbackProjectName,
      fallbackProjectLocation,
      contentfulProjectName,
    );
  }
  return active.unitNumberHint || active.unitId;
}

/** Listing reservation card — bookings + profile `current_status` (same overlay as details). */
function resolveReservedListingCardFromBookingResponse(
  response: BookingResponse | null | undefined,
  listings: ListingUnit[],
  t: PropertyTranslateFn,
  fallbackProjectName?: string,
  fallbackProjectLocation?: string,
  contentfulProjectName?: string,
  profile?: ProfileDetailsResponse | null,
): ReservedListingCard | null {
  const active = parseActiveBookingFromApi(response);
  if (!active) {
    return null;
  }

  const reservationStatus = resolveReservationStatusFromBookingAndProfile(
    active.unitId,
    active.reservationStatus,
    profile,
  );

  const bookingRecord = extractBookingDetails(response) as Record<
    string,
    unknown
  > | null;
  const bookingListingUnit = bookingRecord
    ? availableUnitFromBookingRecord(bookingRecord, reservationStatus)
    : null;
  const matchedListing = listings.find((l) => l.unit_id === active.unitId);
  const unitForStatus: AvailableUnit | null =
    bookingListingUnit ??
    (matchedListing
      ? {
        unit_id: matchedListing.unit_id,
        unit_number: matchedListing.unit_number,
        unit_type: '',
        number_of_bedrooms: matchedListing.number_of_bedrooms,
        number_of_bathrooms: matchedListing.number_of_bathrooms,
        total_area: matchedListing.total_area,
        total_price: matchedListing.total_price,
        latitude: 0,
        longitude: 0,
        reservation_status: reservationStatus,
        ...(matchedListing.handover_date
          ? { handover_date: matchedListing.handover_date }
          : {}),
      }
      : null);

  if (!unitForStatus) {
    return null;
  }

  const mappedStatus = resolveWebReservationMappedStatus(unitForStatus, {
    isLocallyReserved: true,
  });
  if (!mappedStatus) {
    return null;
  }

  const unitTitle = resolveReservedListingUnitTitle(
    matchedListing,
    bookingListingUnit,
    active,
    fallbackProjectName,
    fallbackProjectLocation,
    contentfulProjectName,
  );

  return {
    mappedStatus,
    rawStatus: unitForStatus.reservation_status ?? null,
    unitTitle,
    unitId: active.unitId,
  };
}

/** Appointment / reservation status — optionally shares a row with the price filter. */
function AppointmentToolsRow({
  isMobile,
  isTablet,
  isRTL,
  maxPrice,
  iskanLoanAed,
  onMaxPriceChange,
  statusRowLayout,
  showPriceFilter = true,
  children,
}: Readonly<{
  isMobile: boolean;
  isTablet: boolean;
  isRTL: boolean;
  maxPrice: number;
  iskanLoanAed: number;
  onMaxPriceChange: (value: number) => void;
  statusRowLayout: StatusRowLayout;
  showPriceFilter?: boolean;
  children: React.ReactNode;
}>) {
  const {
    stackStatusRow,
    matchStatusRowHeights,
    statusRowMinHeight,
    useWideDesktopCompactHeights,
    narrowStatusRow,
  } = statusRowLayout;
  /** 1100–1320 (Nest Hub Max, etc.): equal halves — avoids leftover gap from 698+570 caps. */
  const useEqualStatusSliderSplit = showPriceFilter && narrowStatusRow;
  const useSplitSliderWidth = showPriceFilter && isTablet && !stackStatusRow;
  const statusRowMinHeightProps: { statusRowMinHeight?: number } = {};
  if (statusRowMinHeight != null) {
    statusRowMinHeightProps.statusRowMinHeight = statusRowMinHeight;
  }

  return (
    <View
      style={[
        styles.toolsRow,
        (stackStatusRow || !showPriceFilter) && styles.toolsRowStacked,
        matchStatusRowHeights && styles.toolsRowMatchHeights,
        matchStatusRowHeights &&
          !showPriceFilter &&
          styles.toolsRowFillParent,
        useEqualStatusSliderSplit && styles.toolsRowEqualSplit,
        isMobile && !stackStatusRow && showPriceFilter && { justifyContent: "center" },
      ]}
    >
      <View
        style={[
          !showPriceFilter ? styles.statusCardsFullWidth : undefined,
          matchStatusRowHeights && styles.statusCardsMatchHeights,
        ]}
      >
        {children}
      </View>

      {showPriceFilter ? (
      <View
        style={[
          styles.sliderWrap,
          stackStatusRow && styles.sliderWrapStacked,
          matchStatusRowHeights && styles.sliderWrapMatchHeights,
          (useSplitSliderWidth || useEqualStatusSliderSplit) &&
          styles.sliderWrapTablet,
        ]}
      >
        <PriceRangeSlider
          value={maxPrice}
          loanAmountAed={iskanLoanAed}
          onValueChange={onMaxPriceChange}
          fullWidth={
            useSplitSliderWidth || stackStatusRow || useEqualStatusSliderSplit
          }
          fillAvailableHeight={matchStatusRowHeights}
          compactStatusRow={useWideDesktopCompactHeights}
          {...statusRowMinHeightProps}
        />
      </View>
      ) : null}
    </View>
  );
}

type StatusCardActionSizing = {
  useCompactActions: boolean;
  useTightActions: boolean;
};

function getAppointmentActionsRowStyles({
  spreadStatusActions,
  useWideDesktopCompactHeights,
}: {
  spreadStatusActions: boolean;
  useWideDesktopCompactHeights: boolean;
}) {
  // <500: Cancel left, outline right. Otherwise: tight 32px gap, pinned trailing.
  return [
    styles.reservedCardActions,
    styles.reservedCardActionsInline,
    spreadStatusActions
      ? styles.reservedCardActionsMobileSpread
      : styles.reservedCardActionsEnd,
    !spreadStatusActions &&
    useWideDesktopCompactHeights &&
    styles.appointmentRightEndWide,
  ];
}

function getReservedListingActionsRowStyles({
  canCancelReservation,
  spreadStatusActions,
  useWideDesktopCompactHeights,
}: {
  canCancelReservation: boolean;
  spreadStatusActions: boolean;
  useWideDesktopCompactHeights: boolean;
}) {
  if (!canCancelReservation) {
    return [
      styles.reservedCardActions,
      styles.reservedCardActionsInline,
      styles.reservedCardActionsCompact,
    ];
  }

  return [
    styles.reservedCardActions,
    styles.reservedCardActionsInline,
    spreadStatusActions
      ? styles.reservedCardActionsMobileSpread
      : styles.reservedCardActionsEnd,
    !spreadStatusActions &&
    useWideDesktopCompactHeights &&
    styles.appointmentRightEndWide,
  ];
}

function StatusCardCancelAction({
  label,
  sizing,
  textDecorationColor,
  isMobile,
  onPress,
}: Readonly<{
  label: string;
  sizing: StatusCardActionSizing;
  textDecorationColor: string;
  isMobile?: boolean;
  onPress: () => void;
}>) {
  return (
    <Pressable
      style={[
        styles.reservedCancelBtn,
        sizing.useCompactActions && styles.reservedCancelBtnCompact,
        sizing.useTightActions && styles.reservedCancelBtnTight,
        isMobile && styles.reservedCancelBtnMobile,
      ]}
      onPress={onPress}
      accessibilityRole="button"
      accessibilityLabel={label}
    >
      <Typography
        variant="headline"
        color="highContrast"
        style={[
          styles.reservedCancelText,
          sizing.useCompactActions && styles.reservedCancelTextCompact,
          sizing.useTightActions && styles.reservedCancelTextTight,
          isMobile && styles.reservedCancelTextMobile,
          { textDecorationColor },
        ]}
        numberOfLines={isMobile ? 1 : undefined}
      >
        {label}
      </Typography>
    </Pressable>
  );
}

function StatusCardOutlineAction({
  label,
  sizing,
  borderColor,
  isMobile,
  onPress,
}: Readonly<{
  label: string;
  sizing: StatusCardActionSizing;
  borderColor: string;
  isMobile?: boolean;
  onPress: () => void;
}>) {
  return (
    <Pressable
      style={[
        styles.viewReservationBtn,
        sizing.useCompactActions && styles.viewReservationBtnCompact,
        sizing.useTightActions && styles.viewReservationBtnTight,
        isMobile && styles.viewReservationBtnMobile,
        { borderColor },
      ]}
      onPress={onPress}
      accessibilityRole="button"
      accessibilityLabel={label}
    >
      <Typography
        variant="headline"
        color="primary"
        style={[
          styles.viewReservationText,
          sizing.useCompactActions && styles.viewReservationTextCompact,
          sizing.useTightActions && styles.viewReservationTextTight,
        ]}
        numberOfLines={1}
      >
        {label}
      </Typography>
    </Pressable>
  );
}

function AppointmentCardActions({
  labels,
  useCompactActions,
  useTightActions,
  spreadStatusActions,
  useWideDesktopCompactHeights,
  cancelTextDecorationColor,
  rescheduleBorderColor,
  onCancel,
  onReschedule,
}: Readonly<{
  labels: { cancel: string; reschedule: string };
  useCompactActions: boolean;
  useTightActions: boolean;
  spreadStatusActions: boolean;
  useWideDesktopCompactHeights: boolean;
  cancelTextDecorationColor: string;
  rescheduleBorderColor: string;
  onCancel: () => void;
  onReschedule: () => void;
}>) {
  const sizing = { useCompactActions, useTightActions };

  return (
    <View
      style={getAppointmentActionsRowStyles({
        spreadStatusActions,
        useWideDesktopCompactHeights,
      })}
    >
      <StatusCardCancelAction
        label={labels.cancel}
        sizing={sizing}
        textDecorationColor={cancelTextDecorationColor}
        isMobile={spreadStatusActions}
        onPress={onCancel}
      />
      <StatusCardOutlineAction
        label={labels.reschedule}
        sizing={sizing}
        borderColor={rescheduleBorderColor}
        isMobile={spreadStatusActions}
        onPress={onReschedule}
      />
    </View>
  );
}

/* Visit Office | Join Online helpers — temporarily unused while meeting links are hidden
function openAppointmentExternalUrl(url: string): void {
  const href = url.trim();
  if (!href) {
    return;
  }
  if (Platform.OS === 'web' && typeof window !== 'undefined') {
    window.open(href, '_blank', 'noopener,noreferrer');
    return;
  }
  Linking.openURL(href).catch(() => {
    // Non-critical external navigation.
  });
}

function AppointmentMeetingLinksRow({
  visitOfficeLabel,
  joinHereLabel,
  linkColor,
  dividerColor,
  onVisitOffice,
  onJoin,
}: Readonly<{
  visitOfficeLabel: string;
  joinHereLabel: string;
  linkColor: string;
  dividerColor: string;
  onVisitOffice: () => void;
  onJoin: () => void;
}>) {
  return (
    <View style={styles.appointmentMeetingRow}>
      <Pressable
        onPress={onVisitOffice}
        accessibilityRole="link"
        accessibilityLabel={visitOfficeLabel}
        style={styles.appointmentJoinPressable}
      >
        <Typography
          style={[styles.appointmentJoinLink, { color: linkColor }]}
          numberOfLines={1}
        >
          {visitOfficeLabel}
        </Typography>
      </Pressable>
      <View
        style={[styles.appointmentMeetingDivider, { borderColor: dividerColor }]}
        accessibilityElementsHidden
        importantForAccessibility="no-hide-descendants"
      />
      <Pressable
        onPress={onJoin}
        accessibilityRole="link"
        accessibilityLabel={joinHereLabel}
        style={styles.appointmentJoinPressable}
      >
        <Typography
          style={[styles.appointmentJoinLink, { color: linkColor }]}
          numberOfLines={1}
        >
          {joinHereLabel}
        </Typography>
      </Pressable>
    </View>
  );
}

function AppointmentMeetingMeasureProbe({
  visitOfficeLabel,
  joinHereLabel,
  linkColor,
  dividerColor,
  onNaturalWidth,
}: Readonly<{
  visitOfficeLabel: string;
  joinHereLabel: string;
  linkColor: string;
  dividerColor: string;
  onNaturalWidth: (width: number) => void;
}>) {
  return (
    <View
      pointerEvents="none"
      style={styles.appointmentMeetingMeasureProbe}
      accessibilityElementsHidden
      importantForAccessibility="no-hide-descendants"
    >
      <View
        style={styles.appointmentMeetingMeasureRow}
        onLayout={(event) => {
          onNaturalWidth(Math.ceil(event.nativeEvent.layout.width));
        }}
      >
        <Typography
          style={[
            styles.appointmentJoinLink,
            styles.appointmentMeetingMeasureText,
            { color: linkColor },
          ]}
          numberOfLines={1}
        >
          {visitOfficeLabel}
        </Typography>
        <View
          style={[styles.appointmentMeetingDivider, { borderColor: dividerColor }]}
        />
        <Typography
          style={[
            styles.appointmentJoinLink,
            styles.appointmentMeetingMeasureText,
            { color: linkColor },
          ]}
          numberOfLines={1}
        >
          {joinHereLabel}
        </Typography>
      </View>
    </View>
  );
}

function setStableLayoutWidth(
  setter: React.Dispatch<React.SetStateAction<number>>,
  next: number,
): void {
  setter((prev) => (Math.abs(prev - next) <= 1 ? prev : next));
}

function useAppointmentMeetingBelowIcon(
  isMobile: boolean,
  resetKey: string,
): {
  showMeetingBelow: boolean;
  onNaturalWidth: (width: number) => void;
  onDetailsColumnWidth: (width: number) => void;
} {
  const [meetingBelowIcon, setMeetingBelowIcon] = useState(false);
  const [meetingNaturalWidth, setMeetingNaturalWidth] = useState(0);
  const [detailsColumnWidth, setDetailsColumnWidth] = useState(0);

  useEffect(() => {
    setMeetingBelowIcon(false);
    setMeetingNaturalWidth(0);
    setDetailsColumnWidth(0);
  }, [resetKey]);

  useEffect(() => {
    if (!isMobile || meetingNaturalWidth <= 0 || detailsColumnWidth <= 0) {
      return;
    }
    setMeetingBelowIcon(meetingNaturalWidth > detailsColumnWidth + 1);
  }, [isMobile, meetingNaturalWidth, detailsColumnWidth]);

  return {
    showMeetingBelow: isMobile && meetingBelowIcon,
    onNaturalWidth: (width) => setStableLayoutWidth(setMeetingNaturalWidth, width),
    onDetailsColumnWidth: (width) =>
      setStableLayoutWidth(setDetailsColumnWidth, width),
  };
}

*/

function getAppointmentCardContainerStyle(options: {
  stackStatusRow: boolean;
  narrowStatusRow: boolean;
  matchStatusRowHeights: boolean;
  statusRowMinHeight: number | undefined;
  useWideDesktopCompactHeights: boolean;
  spreadStatusActions: boolean;
  isMobile: boolean;
  backgroundColor: string;
  borderColor: string;
}): StyleProp<ViewStyle> {
  const {
    stackStatusRow,
    narrowStatusRow,
    matchStatusRowHeights,
    statusRowMinHeight,
    useWideDesktopCompactHeights,
    spreadStatusActions,
    isMobile,
    backgroundColor,
    borderColor,
  } = options;

  return [
    styles.appointmentCard,
    stackStatusRow && styles.statusCardStackedFullWidth,
    narrowStatusRow && styles.statusCardEqualSplitWidth,
    matchStatusRowHeights && styles.appointmentCardMatchHeights,
    matchStatusRowHeights && styles.statusCardFillHeight,
    matchStatusRowHeights && styles.statusCardStatusRow,
    matchStatusRowHeights &&
      statusRowMinHeight != null && { minHeight: statusRowMinHeight },
    useWideDesktopCompactHeights && styles.statusCardWideDesktop,
    useWideDesktopCompactHeights && styles.statusCardWideDesktopPadding,
    // Keep Cancel/Reschedule beside the details (not under) except narrow mobile.
    !spreadStatusActions && styles.appointmentCardActionsBeside,
    isMobile && styles.appointmentCardMobile,
    { backgroundColor, borderColor },
  ];
}

function AppointmentCardHeader({
  title,
  relativeBadge,
  badgeBg,
  badgeTextColor,
}: Readonly<{
  title: string;
  relativeBadge: string | undefined;
  badgeBg: string;
  badgeTextColor: string;
}>) {
  return (
    <View style={styles.appointmentHeader}>
      <Typography variant="headline" style={styles.appointmentTitle} numberOfLines={1}>
        {title}
      </Typography>
      {relativeBadge ? (
        <View style={[styles.tomorrowBadge, { backgroundColor: badgeBg }]}>
          <Typography
            variant="caption"
            style={[styles.tomorrowText, { color: badgeTextColor }]}
          >
            {relativeBadge}
          </Typography>
        </View>
      ) : null}
    </View>
  );
}

function AppointmentSummaryBullet() {
  return <Text style={styles.appointmentSummaryBullet}>•</Text>;
}

function AppointmentSummaryMeta({
  dateDisplay,
  timeLabel,
  salesContact,
  stackSalesContact,
}: Readonly<{
  dateDisplay: string;
  timeLabel: string;
  salesContact: React.ReactNode;
  stackSalesContact: boolean;
}>) {
  return (
    <View style={styles.appointmentSummaryBlock}>
      <View style={styles.appointmentSummaryRow}>
        <Text style={styles.appointmentSummary} numberOfLines={1}>
          {dateDisplay}
        </Text>
        <AppointmentSummaryBullet />
        <Text style={styles.appointmentSummary} numberOfLines={1}>
          {timeLabel}
        </Text>
        {!stackSalesContact && salesContact ? (
          <>
            <AppointmentSummaryBullet />
            {salesContact}
          </>
        ) : null}
      </View>
      {stackSalesContact && salesContact ? (
        <View style={styles.appointmentSalesNextLine}>{salesContact}</View>
      ) : null}
    </View>
  );
}

function AppointmentCardBody({
  iconBackgroundColor,
  title,
  relativeBadge,
  badgeBg,
  badgeTextColor,
  dateDisplay,
  timeLabel,
  salesContact,
  stackSalesContact,
}: Readonly<{
  iconBackgroundColor: string;
  title: string;
  relativeBadge: string | undefined;
  badgeBg: string;
  badgeTextColor: string;
  dateDisplay: string;
  timeLabel: string;
  salesContact: React.ReactNode;
  stackSalesContact: boolean;
}>) {
  return (
    <View style={styles.appointmentLeft}>
      <View style={styles.appointmentLeftMain}>
        <View
          style={[
            styles.appointmentIconCircle,
            { backgroundColor: iconBackgroundColor },
          ]}
        >
          <AppointmentCalendarIcon size={24} />
        </View>
        <View style={styles.appointmentDetails}>
          <AppointmentCardHeader
            title={title}
            relativeBadge={relativeBadge}
            badgeBg={badgeBg}
            badgeTextColor={badgeTextColor}
          />
          <AppointmentSummaryMeta
            dateDisplay={dateDisplay}
            timeLabel={timeLabel}
            salesContact={salesContact}
            stackSalesContact={stackSalesContact}
          />
        </View>
      </View>
    </View>
  );
}

function AppointmentCard({
  appointment,
  relativeBadge,
  isMobile,
  isTablet,
  statusRowLayout,
  backgroundColor,
  borderColor,
  badgeBg,
  badgeTextColor,
  rescheduleBorderColor,
  cancelTextDecorationColor,
  onCancel,
  onReschedule,
}: Readonly<{
  appointment: BookedAppointment;
  relativeBadge: string | undefined;
  isMobile: boolean;
  isTablet: boolean;
  statusRowLayout: StatusRowLayout;
  backgroundColor: string;
  borderColor: string;
  badgeBg: string;
  badgeTextColor: string;
  rescheduleBorderColor: string;
  cancelTextDecorationColor: string;
  onCancel: () => void;
  onReschedule: () => void;
}>) {
  const { listing: LABELS } = usePropertyLabels();
  const colors = useUIColors();
  const {
    matchStatusRowHeights,
    statusRowMinHeight,
    useWideDesktopCompactHeights,
    spreadStatusActions,
    stackStatusRow,
    narrowStatusRow,
    reservationActionSize,
  } = statusRowLayout;
  const useCompactActions = reservationActionSize === 'compact';
  const useTightActions = reservationActionSize === 'tight';
  const salesAgent = useSalesAgentContact();

  // Date/time only — Account Manager label hidden; agent dial contact is separate.
  const { dateDisplay, timeLabel } = formatAppointmentSummaryParts(appointment);

  const hasSalesAgent =
    salesAgent.name.length > 0 || salesAgent.phone.length > 0;
  const salesContact =
    SHOW_SALES_AGENT_CONTACT && hasSalesAgent ? (
      <AppointmentSalesContact
        label={salesAgent.name}
        phone={salesAgent.phone}
        color={colors.appointmentBadgeText}
      />
    ) : null;

  /* Visit Office | Join Online — temporarily hidden (helpers commented above). */

  return (
    <View
      style={getAppointmentCardContainerStyle({
        stackStatusRow,
        narrowStatusRow,
        matchStatusRowHeights,
        statusRowMinHeight,
        useWideDesktopCompactHeights,
        spreadStatusActions,
        isMobile,
        backgroundColor,
        borderColor,
      })}
    >
      <AppointmentCardBody
        iconBackgroundColor={colors.backgroundLevel2}
        title={LABELS.upcomingAppointment}
        relativeBadge={relativeBadge}
        badgeBg={badgeBg}
        badgeTextColor={badgeTextColor}
        dateDisplay={dateDisplay}
        timeLabel={timeLabel}
        salesContact={salesContact}
        stackSalesContact={isMobile}
      />

      <AppointmentCardActions
        labels={{ cancel: LABELS.cancel, reschedule: LABELS.reschedule }}
        useCompactActions={useCompactActions}
        useTightActions={useTightActions}
        spreadStatusActions={spreadStatusActions}
        useWideDesktopCompactHeights={useWideDesktopCompactHeights}
        cancelTextDecorationColor={cancelTextDecorationColor}
        rescheduleBorderColor={rescheduleBorderColor}
        onCancel={onCancel}
        onReschedule={onReschedule}
      />
    </View>
  );
}

function ReservedListingCardActions({
  canCancelReservation,
  spreadStatusActions,
  useCompactActions,
  useTightActions,
  useWideDesktopCompactHeights,
  cancelLabel,
  viewReservationLabel,
  cancelTextDecorationColor,
  actionBorderColor,
  onCancelReservation,
  onViewReservation,
}: Readonly<{
  canCancelReservation: boolean;
  spreadStatusActions: boolean;
  useCompactActions: boolean;
  useTightActions: boolean;
  useWideDesktopCompactHeights: boolean;
  cancelLabel: string;
  viewReservationLabel: string;
  cancelTextDecorationColor: string;
  actionBorderColor: string;
  onCancelReservation: () => void;
  onViewReservation: () => void;
}>) {
  const sizing = { useCompactActions, useTightActions };

  return (
    <View
      style={getReservedListingActionsRowStyles({
        canCancelReservation,
        spreadStatusActions,
        useWideDesktopCompactHeights,
      })}
    >
      {canCancelReservation ? (
        <StatusCardCancelAction
          label={cancelLabel}
          sizing={sizing}
          textDecorationColor={cancelTextDecorationColor}
          isMobile={spreadStatusActions}
          onPress={onCancelReservation}
        />
      ) : null}
      <StatusCardOutlineAction
        label={viewReservationLabel}
        sizing={sizing}
        borderColor={actionBorderColor}
        isMobile={spreadStatusActions}
        onPress={onViewReservation}
      />
    </View>
  );
}

function UnitReservedCard({
  unitTitle,
  mappedStatus,
  rawStatus,
  isMobile,
  isTablet,
  statusRowLayout,
  backgroundColor,
  borderColor,
  actionBorderColor,
  canCancelReservation,
  onCancelReservation,
  onViewReservation,
}: Readonly<{
  unitTitle: string;
  mappedStatus: ReservationStatus;
  rawStatus?: string | null;
  isMobile: boolean;
  isTablet: boolean;
  statusRowLayout: StatusRowLayout;
  backgroundColor: string;
  borderColor: string;
  actionBorderColor: string;
  canCancelReservation: boolean;
  onCancelReservation: () => void;
  onViewReservation: () => void;
}>) {
  const {
    stackStatusRow,
    narrowStatusRow,
    reservationActionSize,
    matchStatusRowHeights,
    statusRowMinHeight,
    useWideDesktopCompactHeights,
    spreadStatusActions,
  } = statusRowLayout;
  const { listing: LABELS, UNIT_DETAILS: unitLabels } = usePropertyLabels();
  const colors = useUIColors();
  const useCompactActions = reservationActionSize === 'compact';
  const useTightActions = reservationActionSize === 'tight';

  return (
    <View
      style={[
        styles.reservedListingCard,
        stackStatusRow && styles.statusCardStackedFullWidth,
        narrowStatusRow && styles.statusCardEqualSplitWidth,
        matchStatusRowHeights && styles.appointmentCardMatchHeights,
        matchStatusRowHeights && styles.statusCardFillHeight,
        matchStatusRowHeights && styles.statusCardStatusRow,
        matchStatusRowHeights &&
        statusRowMinHeight != null && { minHeight: statusRowMinHeight },
        useWideDesktopCompactHeights && styles.statusCardWideDesktop,
        useWideDesktopCompactHeights && styles.statusCardWideDesktopPadding,
        !canCancelReservation && !stackStatusRow && styles.reservedListingCardCompact,
        { backgroundColor, borderColor },
      ]}
    >
      <View style={styles.reservedListingCardLeft}>
        <UnitReservedConfettiIcon size={40} />
        <View style={styles.appointmentDetails}>
          <View style={styles.appointmentHeader}>
            <Typography style={styles.appointmentTitle} numberOfLines={1}>
              {LABELS.unitReserved}
            </Typography>
          </View>
          {unitTitle ? (
            <Typography style={styles.appointmentSubtext}>{unitTitle}</Typography>
          ) : null}
          <ReservationStatusMessage
            mappedStatus={mappedStatus}
            rawStatus={rawStatus ?? null}
            color="secondary"
            variant="body"
            style={styles.appointmentSubtext}
            skeletonWidth={180}
            skeletonHeight={16}
          />
        </View>
      </View>

      <ReservedListingCardActions
        canCancelReservation={canCancelReservation}
        spreadStatusActions={spreadStatusActions}
        useCompactActions={useCompactActions}
        useTightActions={useTightActions}
        useWideDesktopCompactHeights={useWideDesktopCompactHeights}
        cancelLabel={unitLabels.CANCEL_RESERVATION_TITLE}
        viewReservationLabel={LABELS.viewReservation}
        cancelTextDecorationColor={colors.fontHighContrast}
        actionBorderColor={actionBorderColor}
        onCancelReservation={onCancelReservation}
        onViewReservation={onViewReservation}
      />
    </View>
  );
}

const ListingGridCard = React.memo(function ListingGridCard({
  unit,
  images,
  imagesLoading,
  singleColumn,
  projectNameFromProfile,
  projectLocationFromProfile,
  handoverFromProfile,
  contentfulProjectName,
  onListingPress,
}: Readonly<{
  unit: ListingUnit;
  images: readonly { uri: string }[];
  imagesLoading: boolean;
  singleColumn: boolean;
  projectNameFromProfile: string;
  projectLocationFromProfile: string;
  handoverFromProfile: string;
  contentfulProjectName: string;
  onListingPress: (unitId: string) => void;
}>) {
  const handlePress = React.useCallback(() => {
    onListingPress(unit.unit_id);
  }, [onListingPress, unit.unit_id]);

  return (
    <ListingCard
      fullWidth
      singleColumn={singleColumn}
      title={listingUnitTitle(
        unit,
        projectNameFromProfile,
        projectLocationFromProfile,
        contentfulProjectName,
      )}
      bedrooms={Number.parseInt(unit.number_of_bedrooms, 10)}
      bathrooms={
        typeof unit.number_of_bathrooms === "number"
          ? unit.number_of_bathrooms
          : Number.parseFloat(String(unit.number_of_bathrooms ?? ""))
      }
      sqft={unit.total_area}
      price={unit.total_price}
      handoverDate={unit.handover_date || handoverFromProfile}
      images={images as { uri: string }[]}
      imagesLoading={imagesLoading}
      onPress={handlePress}
    />
  );
});

const ViewportListingCard = React.memo(function ViewportListingCard({
  measure,
  onCardLayout,
  style,
  children,
}: Readonly<{
  measure: boolean;
  onCardLayout: (height: number) => void;
  style: StyleProp<ViewStyle>;
  children: React.ReactNode;
}>) {
  return (
    <View
      style={style}
      {...(measure
        ? {
            onLayout: (event: { nativeEvent: { layout: { height: number } } }) =>
              onCardLayout(event.nativeEvent.layout.height),
          }
        : null)}
    >
      {children}
    </View>
  );
});

function ListingsGridContent({
  showSkeleton,
  displayListings,
  colWidth,
  singleColumn,
  columnCount,
  projectNameFromProfile,
  projectLocationFromProfile,
  handoverFromProfile,
  unitImageLookup,
  imagesLoading,
  contentfulProjectName,
  hasMaxPriceFilter,
  isUnitsError,
  isLoadingMore,
  onListingPress,
  listKey,
}: Readonly<{
  showSkeleton: boolean;
  displayListings: ListingUnit[];
  colWidth: DimensionValue;
  singleColumn: boolean;
  columnCount: number;
  projectNameFromProfile: string;
  projectLocationFromProfile: string;
  handoverFromProfile: string;
  unitImageLookup: Readonly<Record<string, readonly PropertyImage[]>> | null;
  imagesLoading: boolean;
  contentfulProjectName: string;
  hasMaxPriceFilter: boolean;
  isUnitsError: boolean;
  isLoadingMore: boolean;
  onListingPress: (unitId: string) => void;
  listKey: string;
}>) {
  const { listing } = usePropertyLabels();
  const colors = useUIColors();
  // A new page renders as background work so its slots never block scrolling.
  // The first list renders at once so the grid is never empty while units exist.
  // The key is deferred with the list so a reset always matches the units it describes.
  const currentGrid = useMemo(
    () => ({ units: displayListings, listKey }),
    [displayListings, listKey],
  );
  const deferredGrid = useDeferredValue(currentGrid);
  const grid = deferredGrid.units.length > 0 ? deferredGrid : currentGrid;
  const gridListings = grid.units;
  const {
    mappedUnitIds,
    listingImagesByUnitId,
    blockRevisions,
    listEpoch,
    cardHeight,
    onCardLayout,
  } = useViewportListingCards(gridListings, columnCount, unitImageLookup, grid.listKey);
  const itemStyle = useMemo(
    () => [styles.gridItem, { width: colWidth }],
    [colWidth],
  );

  if (showSkeleton) {
    return (
      <>
        {(
          [
            'listing-skeleton-1',
            'listing-skeleton-2',
            'listing-skeleton-3',
            'listing-skeleton-4',
            'listing-skeleton-5',
            'listing-skeleton-6',
          ] as const
        ).map((skeletonKey) => (
          <View key={skeletonKey} style={itemStyle}>
            <ListingCardSkeleton fullWidth singleColumn={singleColumn} />
          </View>
        ))}
      </>
    );
  }

  if (displayListings.length === 0) {
    const emptyCopy = getListingEmptyCopy(
      resolveListingEmptyKind(isUnitsError, hasMaxPriceFilter),
      listing,
    );
    return (
      <ListingEmptyState
        title={emptyCopy.title}
        subtitle={emptyCopy.subtitle}
      />
    );
  }

  return (
    <View style={styles.viewportWindow}>
      <View style={styles.grid}>
      {blockRevisions.map((revision, block) => (
        <ListingSlotBlock
          key={`${listEpoch}-${block}`}
          units={gridListings}
          start={block * LISTING_SLOT_BLOCK_SIZE}
          revision={revision}
          listEpoch={listEpoch}
          mappedUnitIds={mappedUnitIds}
          listingImagesByUnitId={listingImagesByUnitId}
          imagesLoading={imagesLoading}
          itemStyle={itemStyle}
          colWidth={colWidth}
          skeletonHeight={cardHeight}
          skeletonBaseColor={colors.backgroundLevel2}
          skeletonHighlightColor={colors.backgroundLevel4}
          singleColumn={singleColumn}
          projectNameFromProfile={projectNameFromProfile}
          projectLocationFromProfile={projectLocationFromProfile}
          handoverFromProfile={handoverFromProfile}
          contentfulProjectName={contentfulProjectName}
          onListingPress={onListingPress}
          onCardLayout={onCardLayout}
        />
      ))}
      {isLoadingMore
        ? NEXT_PAGE_SKELETON_KEYS.map((skeletonKey) => (
          <View key={skeletonKey} style={itemStyle}>
            <ListingCardSkeleton fullWidth singleColumn={singleColumn} />
          </View>
        ))
        : null}
      </View>
    </View>
  );
}

type ListingSlotBlockProps = Readonly<{
  units: ListingUnit[];
  start: number;
  revision: number;
  listEpoch: number;
  mappedUnitIds: ReadonlySet<string>;
  listingImagesByUnitId: Readonly<Record<string, { uri: string }[]>>;
  imagesLoading: boolean;
  itemStyle: StyleProp<ViewStyle>;
  colWidth: DimensionValue;
  skeletonHeight: number;
  skeletonBaseColor: string;
  skeletonHighlightColor: string;
  singleColumn: boolean;
  projectNameFromProfile: string;
  projectLocationFromProfile: string;
  handoverFromProfile: string;
  contentfulProjectName: string;
  onListingPress: (unitId: string) => void;
  onCardLayout: (height: number) => void;
}>;

function blockLength(props: ListingSlotBlockProps): number {
  return Math.max(
    0,
    Math.min(props.units.length, props.start + LISTING_SLOT_BLOCK_SIZE) - props.start,
  );
}

/**
 * `units`, `mappedUnitIds` and `listingImagesByUnitId` change identity on every
 * append; `revision` already says whether anything in this block changed.
 */
function sameSlotBlock(prev: ListingSlotBlockProps, next: ListingSlotBlockProps): boolean {
  return (
    prev.revision === next.revision &&
    prev.imagesLoading === next.imagesLoading &&
    prev.listEpoch === next.listEpoch &&
    prev.start === next.start &&
    blockLength(prev) === blockLength(next) &&
    prev.itemStyle === next.itemStyle &&
    prev.colWidth === next.colWidth &&
    prev.skeletonHeight === next.skeletonHeight &&
    prev.skeletonBaseColor === next.skeletonBaseColor &&
    prev.skeletonHighlightColor === next.skeletonHighlightColor &&
    prev.singleColumn === next.singleColumn &&
    prev.projectNameFromProfile === next.projectNameFromProfile &&
    prev.projectLocationFromProfile === next.projectLocationFromProfile &&
    prev.handoverFromProfile === next.handoverFromProfile &&
    prev.contentfulProjectName === next.contentfulProjectName &&
    prev.onListingPress === next.onListingPress &&
    prev.onCardLayout === next.onCardLayout
  );
}

const ListingSlotBlock = React.memo(function ListingSlotBlock(props: ListingSlotBlockProps) {
  const { units, start, mappedUnitIds, listingImagesByUnitId } = props;
  const skeletonStyles = useMemo(
    () => htmlSkeletonStyles(props.colWidth, props.skeletonHeight, props.singleColumn, props.skeletonBaseColor, props.skeletonHighlightColor),
    [props.colWidth, props.skeletonHeight, props.singleColumn, props.skeletonBaseColor, props.skeletonHighlightColor],
  );
  const end = start + blockLength(props);
  const slots: React.ReactNode[] = [];
  for (let index = start; index < end; index += 1) {
    const unit = units[index];
    if (!unit) {
      continue;
    }
    if (!mappedUnitIds.has(unit.unit_id)) {
      slots.push(
        <HtmlSkeletonSlot key={unit.unit_id} styles={skeletonStyles} />,
      );
      continue;
    }
    slots.push(
      <ListingUnitSlot
        key={unit.unit_id}
        unit={unit}
        measure={index === 0}
        images={listingImagesByUnitId[unit.unit_id] ?? EMPTY_CARD_IMAGES}
        imagesLoading={props.imagesLoading}
        itemStyle={props.itemStyle}
        singleColumn={props.singleColumn}
        projectNameFromProfile={props.projectNameFromProfile}
        projectLocationFromProfile={props.projectLocationFromProfile}
        handoverFromProfile={props.handoverFromProfile}
        contentfulProjectName={props.contentfulProjectName}
        onListingPress={props.onListingPress}
        onCardLayout={props.onCardLayout}
      />,
    );
  }
  return <>{slots}</>;
}, sameSlotBlock);

type HtmlSkeletonStyles = Readonly<{
  card: React.CSSProperties;
  shimmer: React.CSSProperties;
}>;

/** ListingCardSkeleton box model (padding 8, content inset 12, footer 46px tall). */
const SK_PAD = 8;
const SK_INSET = SK_PAD + 12;
const SK_FOOTER_HEIGHT = 46;
const SHIMMER_CLASS = 'listing-slot-skeleton-shimmer';

function ensureShimmerStylesheet(): void {
  if (typeof document === 'undefined' || document.getElementById(SHIMMER_CLASS)) {
    return;
  }
  const style = document.createElement('style');
  style.id = SHIMMER_CLASS;
  // Same 2s left-to-right sweep as the Skeleton atom on web.
  style.textContent = `
@keyframes ${SHIMMER_CLASS} { from { background-position: -100% 0, 0 0; } to { background-position: 200% 0, 0 0; } }
.${SHIMMER_CLASS} { animation: ${SHIMMER_CLASS} 2s linear infinite; }
@media (prefers-reduced-motion: reduce) { .${SHIMMER_CLASS} { animation: none; } }`;
  document.head.appendChild(style);
}

function svgUrl(width: number, height: number, body: string): string {
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="${width}" height="${height}">${body}</svg>`;
  return `url("data:image/svg+xml,${encodeURIComponent(svg)}")`;
}

function svgRect(x: number, y: number, width: number, height: number, radius: number, fill = '#000'): string {
  return `<rect x="${x}" y="${y}" width="${Math.max(0, width)}" height="${height}" rx="${radius}" fill="${fill}"/>`;
}

/**
 * Pixel copy of ListingCardSkeleton (photo, title, three spec pills, footer panel
 * with price + handover pills, shimmer) drawn with two DOM elements, so a
 * 350-unit page stays cheap. Blocks are an SVG mask over the shimmering layer.
 */
/** Plain CSS width; non-primitive DimensionValues (animated, null) fill the column. */
function toCssWidth(width: DimensionValue): number | string {
  if (typeof width === 'number' || typeof width === 'string') {
    return width;
  }
  return '100%';
}

function htmlSkeletonStyles(
  colWidth: DimensionValue,
  height: number,
  singleColumn: boolean,
  baseColor: string,
  highlightColor: string,
): HtmlSkeletonStyles {
  ensureShimmerStylesheet();
  const mediaHeight = singleColumn ? 260 : 200;
  const card: React.CSSProperties = {
    position: 'relative',
    boxSizing: 'border-box',
    flexShrink: 0,
    width: toCssWidth(colWidth),
    maxWidth: '100%',
    height,
    borderRadius: 12,
    overflow: 'hidden',
    background: '#FFFFFF',
    contentVisibility: 'auto',
  };
  const shimmer: React.CSSProperties = {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: [
      `linear-gradient(90deg, transparent, color-mix(in srgb, ${highlightColor} 85%, transparent), transparent) 0 0 / 50% 100% no-repeat`,
      baseColor,
    ].join(', '),
  };
  if (typeof colWidth !== 'number') {
    // Column not measured yet: photo block only, until the real width is known.
    return {
      card,
      shimmer: {
        ...shimmer,
        top: SK_PAD,
        left: SK_PAD,
        right: SK_PAD,
        bottom: 'auto',
        height: mediaHeight,
        borderRadius: 8,
      },
    };
  }

  const width = colWidth;
  const contentWidth = width - SK_INSET * 2;
  const titleTop = SK_PAD + mediaHeight + SK_PAD + 10;
  const specsTop = titleTop + 25 + 12;
  const specWidth = (contentWidth / 3 - 8) * 0.8;
  const footerTop = height - SK_PAD - SK_FOOTER_HEIGHT;
  const blocks = [
    svgRect(SK_PAD, SK_PAD, width - SK_PAD * 2, mediaHeight, 8),
    svgRect(SK_INSET, titleTop, contentWidth * 0.6, 25, 4),
    svgRect(SK_INSET, specsTop, specWidth, 18, 4),
    svgRect(SK_INSET + contentWidth / 3, specsTop, specWidth, 18, 4),
    svgRect(SK_INSET + (contentWidth * 2) / 3, specsTop, specWidth, 18, 4),
    svgRect(SK_INSET + 16, footerTop + 12, 120, 22, 4),
    svgRect(width - SK_INSET - 16 - 100, footerTop + 14, 100, 18, 4),
  ].join('');
  const mask = svgUrl(width, height, blocks);
  const footerPanel = svgUrl(
    width,
    height,
    svgRect(SK_INSET, footerTop, contentWidth, SK_FOOTER_HEIGHT, 8, baseColor),
  );
  return {
    card: {
      ...card,
      background: `${footerPanel} 0 0 / 100% 100% no-repeat, #FFFFFF`,
      containIntrinsicSize: `${width}px ${height}px`,
    },
    shimmer: {
      ...shimmer,
      maskImage: mask,
      WebkitMaskImage: mask,
      maskSize: '100% 100%',
      WebkitMaskSize: '100% 100%',
      maskRepeat: 'no-repeat',
      WebkitMaskRepeat: 'no-repeat',
    },
  };
}

/**
 * Two plain DOM elements per unmapped slot: a page appends hundreds of these, and
 * the animated RN-web ListingCardSkeleton costs too much per instance at that count.
 * `content-visibility: auto` skips painting and animating slots that are off screen.
 */
const HtmlSkeletonSlot = React.memo(function HtmlSkeletonSlot({
  styles: skeletonStyles,
}: Readonly<{ styles: HtmlSkeletonStyles }>) {
  return (
    <div style={skeletonStyles.card} aria-hidden="true">
      <div className={SHIMMER_CLASS} style={skeletonStyles.shimmer} />
    </div>
  );
});

const ListingUnitSlot = React.memo(function ListingUnitSlot({
  unit,
  measure,
  images,
  imagesLoading,
  itemStyle,
  singleColumn,
  projectNameFromProfile,
  projectLocationFromProfile,
  handoverFromProfile,
  contentfulProjectName,
  onListingPress,
  onCardLayout,
}: Readonly<{
  unit: ListingUnit;
  measure: boolean;
  images: { uri: string }[];
  imagesLoading: boolean;
  itemStyle: StyleProp<ViewStyle>;
  singleColumn: boolean;
  projectNameFromProfile: string;
  projectLocationFromProfile: string;
  handoverFromProfile: string;
  contentfulProjectName: string;
  onListingPress: (unitId: string) => void;
  onCardLayout: (height: number) => void;
}>) {
  return (
    <ViewportListingCard
      style={itemStyle}
      measure={measure}
      onCardLayout={onCardLayout}
    >
      <ListingGridCard
        unit={unit}
        images={images}
        imagesLoading={imagesLoading}
        singleColumn={singleColumn}
        projectNameFromProfile={projectNameFromProfile}
        projectLocationFromProfile={projectLocationFromProfile}
        handoverFromProfile={handoverFromProfile}
        contentfulProjectName={contentfulProjectName}
        onListingPress={onListingPress}
      />
    </ViewportListingCard>
  );
});

function BookAppointmentFab({
  docked,
  onPress,
}: Readonly<{
  /** Mobile / tab layout: Figma edge-docked chip (ignores shell padding). */
  docked: boolean;
  onPress: () => void;
}>) {
  const { listing: LABELS } = usePropertyLabels();
  const { isRTL } = useI18nContext();

  return (
    <View
      style={[
        styles.floatingButtonContainer,
        docked && styles.floatingButtonContainerDocked,
        docked &&
          (isRTL
            ? styles.floatingButtonContainerDockedRTL
            : styles.floatingButtonContainerDockedLTR),
        Platform.OS === 'web' && !docked && isRTL
          ? styles.floatingButtonContainerWebRtl
          : null,
      ]}
    >
      <Pressable
        style={[
          styles.floatingButton,
          docked && styles.floatingButtonDocked,
          docked &&
            (isRTL ? styles.floatingButtonDockedRTL : styles.floatingButtonDockedLTR),
        ]}
        onPress={onPress}
        accessibilityRole="button"
        accessibilityLabel={LABELS.bookAppointment}
      >
        <CalendarDaysIcon size={docked ? 20 : 24} color="#FFFFFF" />
        <Typography
          style={[
            styles.floatingButtonText,
            docked && styles.floatingButtonTextDocked,
          ]}
        >
          {LABELS.bookAppointment}
        </Typography>
      </Pressable>
    </View>
  );
}

type ListingStatusCardBuildParams = Readonly<{
  reservedListingCard: ReservedListingCard | null;
  isBookingLoading: boolean;
  isUpcomingLoading: boolean;
  appointment: BookedAppointment | null | undefined;
  relativeBadge: string | undefined;
  isMobile: boolean;
  isTablet: boolean;
  statusRowLayout: StatusRowLayout;
  colors: ReturnType<typeof useUIColors>;
  onCancel: () => void;
  onReschedule: () => void;
  onViewReservation: () => void;
  onCancelReservation: () => void;
  canCancelReservedListing: boolean;
  carouselCardWrapStyle: StyleProp<ViewStyle>;
}>;

function useStatusCarouselAutoPlay({
  enabled,
  cardWidth,
  cardCount,
  scrollToCarouselIndex,
  setActiveIndex,
}: Readonly<{
  enabled: boolean;
  cardWidth: number;
  cardCount: number;
  scrollToCarouselIndex: (index: number, animated: boolean) => void;
  setActiveIndex: React.Dispatch<React.SetStateAction<number>>;
}>) {
  useEffect(() => {
    if (!enabled) {
      return undefined;
    }
    const timer = setInterval(() => {
      if (cardWidth <= 0) {
        return;
      }
      setActiveIndex(current => {
        const nextIndex = (current + 1) % cardCount;
        scrollToCarouselIndex(nextIndex, true);
        return nextIndex;
      });
    }, 3000);
    return () => clearInterval(timer);
  }, [cardWidth, enabled, cardCount, scrollToCarouselIndex, setActiveIndex]);
}

/** Pure helper — keeps ListingStatusCardView cognitive complexity ≤ 15. */
function buildListingStatusCards({
  reservedListingCard,
  isBookingLoading,
  isUpcomingLoading,
  appointment,
  relativeBadge,
  isMobile,
  isTablet,
  statusRowLayout,
  colors,
  onCancel,
  onReschedule,
  onViewReservation,
  onCancelReservation,
  canCancelReservedListing,
  carouselCardWrapStyle,
}: ListingStatusCardBuildParams): React.ReactElement[] {
  const cards: React.ReactElement[] = [];
  const showAppointmentSlot = isUpcomingLoading || appointment != null;

  if (showAppointmentSlot) {
    if (isUpcomingLoading) {
      cards.push(
        <View key="appointment-skeleton" style={carouselCardWrapStyle}>
          <AppointmentCardSkeleton
            isMobile={isMobile}
            isTablet={isTablet}
            statusRowLayout={statusRowLayout}
          />
        </View>,
      );
    } else if (appointment) {
      cards.push(
        <View key="appointment" style={carouselCardWrapStyle}>
          <AppointmentCard
            appointment={appointment}
            relativeBadge={relativeBadge}
            isMobile={isMobile}
            isTablet={isTablet}
            statusRowLayout={statusRowLayout}
            backgroundColor={colors.backgroundLevel1}
            borderColor={colors.outlineInactive}
            badgeBg={colors.appointmentBadgeBg}
            badgeTextColor={colors.appointmentBadgeText}
            rescheduleBorderColor={colors.fontPrimary}
            cancelTextDecorationColor={colors.fontHighContrast}
            onCancel={onCancel}
            onReschedule={onReschedule}
          />
        </View>,
      );
    }
  }

  const showReservationSlot = isBookingLoading || reservedListingCard != null;

  if (showReservationSlot) {
    if (isBookingLoading) {
      cards.push(
        <View key="reserved-skeleton" style={carouselCardWrapStyle}>
          <AppointmentCardSkeleton
            isMobile={isMobile}
            isTablet={isTablet}
            statusRowLayout={statusRowLayout}
          />
        </View>,
      );
    } else if (reservedListingCard) {
      cards.push(
        <View key="reserved" style={carouselCardWrapStyle}>
          <UnitReservedCard
            unitTitle={reservedListingCard.unitTitle}
            mappedStatus={reservedListingCard.mappedStatus}
            rawStatus={reservedListingCard.rawStatus}
            isMobile={isMobile}
            isTablet={isTablet}
            statusRowLayout={statusRowLayout}
            backgroundColor={colors.backgroundLevel1}
            borderColor={colors.outlineInactive}
            actionBorderColor={colors.buttonDefault}
            canCancelReservation={canCancelReservedListing}
            onCancelReservation={onCancelReservation}
            onViewReservation={onViewReservation}
          />
        </View>,
      );
    }
  }

  return cards;
}

function resolveListingStatusCardSlotMinHeight(options: {
  matchedStatusCardMinHeight?: number | undefined;
  matchStatusRowHeights: boolean;
  useGridMatchWithPrice: boolean;
  statusRowMinHeight?: number | undefined;
}): number | undefined {
  const {
    matchedStatusCardMinHeight,
    matchStatusRowHeights,
    useGridMatchWithPrice,
    statusRowMinHeight,
  } = options;
  if (matchedStatusCardMinHeight != null) {
    return matchedStatusCardMinHeight;
  }
  if (matchStatusRowHeights && !useGridMatchWithPrice) {
    return statusRowMinHeight;
  }
  return undefined;
}

function ListingStatusCardPriceGrid({
  cardTrack,
  priceFilterSlot,
  belowPriceSlot,
  onBelowPriceOverflowChange,
  dotsRow,
  showCarouselDots,
  onCardWidthChange,
}: Readonly<{
  cardTrack: React.ReactNode;
  priceFilterSlot: React.ReactNode;
  belowPriceSlot?: React.ReactNode;
  onBelowPriceOverflowChange?: ((overflow: number) => void) | undefined;
  dotsRow: React.ReactNode;
  showCarouselDots: boolean;
  onCardWidthChange: (width: number) => void;
}>) {
  const [gridHeight, setGridHeight] = React.useState(0);
  const [priceHeight, setPriceHeight] = React.useState(0);
  const [belowHeight, setBelowHeight] = React.useState(0);

  useEffect(() => {
    if (!onBelowPriceOverflowChange) {
      return;
    }
    if (!belowPriceSlot || belowHeight === 0) {
      onBelowPriceOverflowChange(0);
      return;
    }
    const rowTwoHeight = Math.max(0, gridHeight - priceHeight);
    onBelowPriceOverflowChange(Math.max(0, belowHeight - rowTwoHeight));
  }, [belowHeight, belowPriceSlot, gridHeight, onBelowPriceOverflowChange, priceHeight]);

  return (
    <View
      style={styles.cardPriceMatchGridInner}
      onLayout={(e) => setGridHeight(e.nativeEvent.layout.height)}
    >
      <View
        style={styles.cardPriceMatchGridCard}
        onLayout={(e) => onCardWidthChange(e.nativeEvent.layout.width)}
      >
        {cardTrack}
      </View>
      <View
        style={styles.cardPriceMatchGridPrice}
        onLayout={(e) => setPriceHeight(e.nativeEvent.layout.height)}
      >
        {priceFilterSlot}
      </View>
      {showCarouselDots ? (
        <View style={styles.cardPriceMatchGridDots}>{dotsRow}</View>
      ) : null}
      {belowPriceSlot ? (
        <View style={styles.cardPriceMatchGridBelowPrice}>
          <View
            style={styles.belowPriceOverlay}
            onLayout={(e) => setBelowHeight(e.nativeEvent.layout.height)}
          >
            {belowPriceSlot}
          </View>
        </View>
      ) : null}
    </View>
  );
}

function ListingStatusCardView({
  reservedListingCard,
  isBookingLoading,
  isUpcomingLoading,
  appointment,
  relativeBadge,
  isMobile,
  isTablet,
  isRTL,
  statusRowLayout,
  colors,
  onCancel,
  onReschedule,
  onViewReservation,
  onCancelReservation,
  canCancelReservedListing,
  isAnyDialogOpen,
  matchedStatusCardMinHeight,
  onStatusCardsHeightChange,
  priceFilterSlot,
  belowPriceSlot,
  onBelowPriceOverflowChange,
}: Readonly<{
  reservedListingCard: ReservedListingCard | null;
  isBookingLoading: boolean;
  isUpcomingLoading: boolean;
  appointment: BookedAppointment | null | undefined;
  relativeBadge: string | undefined;
  isMobile: boolean;
  isTablet: boolean;
  isRTL: boolean;
  statusRowLayout: StatusRowLayout;
  colors: ReturnType<typeof useUIColors>;
  onCancel: () => void;
  onReschedule: () => void;
  onViewReservation: () => void;
  onCancelReservation: () => void;
  canCancelReservedListing: boolean;
  isAnyDialogOpen: boolean;
  matchedStatusCardMinHeight?: number | undefined;
  onStatusCardsHeightChange?: ((height: number) => void) | undefined;
  /** ADHA desktop: price sits in CSS grid row1 beside the card (equal height). */
  priceFilterSlot?: React.ReactNode;
  /** ADHA desktop: pinned under the price card, outside grid flow (row heights untouched). */
  belowPriceSlot?: React.ReactNode;
  /** Px the below-price card extends past the bottom of the status row. */
  onBelowPriceOverflowChange?: ((overflow: number) => void) | undefined;
}>) {
  const isFocused = useIsFocused();
  const scrollViewRef = React.useRef<ScrollView>(null);
  const [activeIndex, setActiveIndex] = React.useState(0);
  const [cardWidth, setCardWidth] = React.useState(0);
  const {
    stackStatusRow,
    matchStatusRowHeights,
    narrowStatusRow,
    statusRowMinHeight,
  } = statusRowLayout;
  const useGridMatchWithPrice = priceFilterSlot != null;
  const cardWrapStyle =
    cardWidth > 0 ? { width: cardWidth } : styles.carouselCardMeasured;
  const carouselCardWrapStyle = [
    cardWrapStyle,
    styles.carouselCardWrapGrow,
    (matchStatusRowHeights || useGridMatchWithPrice) &&
      styles.carouselCardWrapMatchHeights,
  ];

  const cards = buildListingStatusCards({
    reservedListingCard,
    isBookingLoading,
    isUpcomingLoading,
    appointment,
    relativeBadge,
    isMobile,
    isTablet,
    statusRowLayout: useGridMatchWithPrice
      ? { ...statusRowLayout, matchStatusRowHeights: true }
      : statusRowLayout,
    colors,
    onCancel,
    onReschedule,
    onViewReservation,
    onCancelReservation,
    canCancelReservedListing,
    carouselCardWrapStyle,
  });

  const showCarouselDots = cards.length > 1;
  const enableCarouselAutoPlay = showCarouselDots && isFocused && !isAnyDialogOpen;

  const scrollToCarouselIndex = React.useCallback(
    (index: number, animated: boolean) => {
      if (cardWidth <= 0) {
        return;
      }
      scrollViewRef.current?.scrollTo({
        x: getStatusCarouselScrollOffset(index, cardWidth, isRTL),
        animated,
      });
    },
    [cardWidth, isRTL],
  );

  useEffect(() => {
    setActiveIndex(0);
    if (cards.length > 1 && cardWidth > 0) {
      scrollToCarouselIndex(0, false);
    }
  }, [isRTL, cardWidth, cards.length, scrollToCarouselIndex]);

  useStatusCarouselAutoPlay({
    enabled: enableCarouselAutoPlay,
    cardWidth,
    cardCount: cards.length,
    scrollToCarouselIndex,
    setActiveIndex,
  });

  useEffect(() => {
    if (cards.length <= 1) {
      setActiveIndex(0);
    }
  }, [cards.length]);

  const handleScroll = (e: {
    nativeEvent: { contentOffset: { x: number } };
  }) => {
    const nextIndex = resolveStatusCarouselActiveIndex(
      e.nativeEvent.contentOffset.x,
      cardWidth,
      cards.length,
      isRTL,
    );
    if (nextIndex !== activeIndex) {
      setActiveIndex(nextIndex);
    }
  };

  const handleDotPress = (index: number) => {
    if (cardWidth > 0) {
      scrollToCarouselIndex(index, true);
      setActiveIndex(index);
    }
  };

  if (cards.length === 0) {
    return null;
  }

  const cardSlotMinHeight = resolveListingStatusCardSlotMinHeight({
    matchedStatusCardMinHeight,
    matchStatusRowHeights,
    useGridMatchWithPrice,
    statusRowMinHeight,
  });

  const cardTrack = (
    <View
      style={[
        (matchStatusRowHeights || useGridMatchWithPrice) &&
          styles.carouselCardsMatchSlot,
        useGridMatchWithPrice && styles.carouselCardsMatchSlotGrid,
        cardSlotMinHeight != null && {
          minHeight: cardSlotMinHeight,
        },
      ]}
      onLayout={(event) => {
        if (!useGridMatchWithPrice) {
          onStatusCardsHeightChange?.(event.nativeEvent.layout.height);
        }
      }}
    >
      <ListingStatusCarouselTrack
        cards={cards}
        cardWidth={cardWidth}
        matchStatusRowHeights={matchStatusRowHeights || useGridMatchWithPrice}
        scrollViewRef={scrollViewRef}
        onScroll={handleScroll}
      />
    </View>
  );

  const dotsRow = showCarouselDots ? (
    <View style={styles.dotsContainer}>
      {cards.map((card, index) => (
        <Pressable
          key={`dot-${card.key}`}
          onPress={() => handleDotPress(index)}
          style={styles.statusCarouselDotHit}
          accessibilityRole="button"
          accessibilityState={{ selected: activeIndex === index }}
        >
          <View
            style={[
              styles.statusCarouselDot,
              activeIndex === index && styles.statusCarouselDotActive,
            ]}
          />
        </Pressable>
      ))}
    </View>
  ) : null;

  if (useGridMatchWithPrice) {
    return (
      <ListingStatusCardPriceGrid
        cardTrack={cardTrack}
        priceFilterSlot={priceFilterSlot}
        belowPriceSlot={belowPriceSlot}
        onBelowPriceOverflowChange={onBelowPriceOverflowChange}
        dotsRow={dotsRow}
        showCarouselDots={showCarouselDots}
        onCardWidthChange={setCardWidth}
      />
    );
  }

  return (
    <View
      style={[
        styles.carouselContainer,
        stackStatusRow && styles.carouselContainerStacked,
        narrowStatusRow && styles.carouselContainerEqualSplit,
      ]}
      onLayout={(e) => setCardWidth(e.nativeEvent.layout.width)}
    >
      {cardTrack}
      {dotsRow}
    </View>
  );
}

function resolveStatusCarouselActiveIndex(
  contentOffsetX: number,
  cardWidth: number,
  cardCount: number,
  isRTL: boolean,
): number {
  if (cardWidth <= 0 || cardCount <= 0) {
    return 0;
  }
  const scrollPosition = getStatusCarouselScrollX(contentOffsetX, isRTL);
  return Math.min(
    Math.max(0, Math.round(scrollPosition / cardWidth)),
    cardCount - 1,
  );
}

function ListingStatusCarouselTrack({
  cards,
  cardWidth,
  matchStatusRowHeights,
  scrollViewRef,
  onScroll,
}: Readonly<{
  cards: React.ReactElement[];
  cardWidth: number;
  matchStatusRowHeights: boolean;
  scrollViewRef: React.RefObject<ScrollView | null>;
  onScroll: (e: {
    nativeEvent: { contentOffset: { x: number } };
  }) => void;
}>) {
  if (cards.length === 1) {
    return (
      <View
        style={matchStatusRowHeights && styles.carouselSingleCardSlot}
      >
        {cards[0]}
      </View>
    );
  }

  return (
    <ScrollView
      ref={scrollViewRef}
      horizontal
      pagingEnabled
      showsHorizontalScrollIndicator={false}
      onScroll={onScroll}
      onMomentumScrollEnd={onScroll}
      scrollEventThrottle={16}
      style={
        matchStatusRowHeights
          ? styles.carouselScrollView
          : styles.carouselScrollViewStacked
      }
    >
      {cards}
    </ScrollView>
  );
}

function PropertiesListHeader({
  greeting,
  showStatusRow,
  isUpcomingLoading,
  reservedListingCard,
  isBookingLoading,
  appointment,
  relativeBadge,
  isMobile,
  isTablet,
  statusRowLayout,
  isRTL,
  colors,
  maxPrice,
  iskanLoanAed,
  onMaxPriceChange,
  onCancel,
  onReschedule,
  onViewReservation,
  onCancelReservation,
  canCancelReservedListing,
  isAnyDialogOpen,
  hidePriceFilter = false,
  hideGreeting = false,
  matchedStatusCardMinHeight,
  onStatusCardsHeightChange,
}: Readonly<{
  greeting: string;
  showStatusRow: boolean;
  isUpcomingLoading: boolean;
  reservedListingCard: ReservedListingCard | null;
  isBookingLoading: boolean;
  appointment: BookedAppointment | null | undefined;
  relativeBadge: string | undefined;
  isMobile: boolean;
  isTablet: boolean;
  statusRowLayout: StatusRowLayout;
  isRTL: boolean;
  colors: ReturnType<typeof useUIColors>;
  maxPrice: number;
  iskanLoanAed: number;
  onMaxPriceChange: (value: number) => void;
  onCancel: () => void;
  onReschedule: () => void;
  onViewReservation: () => void;
  onCancelReservation: () => void;
  canCancelReservedListing: boolean;
  isAnyDialogOpen: boolean;
  /** When true, price slider is omitted (shown in the units column instead). */
  hidePriceFilter?: boolean;
  /** When true, greeting row is omitted (shown in sticky header above the split). */
  hideGreeting?: boolean;
  matchedStatusCardMinHeight?: number | undefined;
  onStatusCardsHeightChange?: ((height: number) => void) | undefined;
}>) {
  const showInlinePrice = !hidePriceFilter && !showStatusRow;
  const matchStatusRowHeights = statusRowLayout.matchStatusRowHeights;

  return (
    <View
      style={[
        styles.headerBlock,
        hideGreeting && styles.headerBlockStatusOnly,
        matchStatusRowHeights && styles.headerBlockMatchHeights,
      ]}
    >
      <PropertiesListGreetingRow
        greeting={greeting}
        hideGreeting={hideGreeting}
        showInlinePrice={showInlinePrice}
        showStatusRow={showStatusRow}
        hidePriceFilter={hidePriceFilter}
        isMobile={isMobile}
        isTablet={isTablet}
        isRTL={isRTL}
        maxPrice={maxPrice}
        iskanLoanAed={iskanLoanAed}
        onMaxPriceChange={onMaxPriceChange}
      />

      {showStatusRow ? (
        <AppointmentToolsRow
          isMobile={isMobile}
          isTablet={isTablet}
          isRTL={isRTL}
          maxPrice={maxPrice}
          iskanLoanAed={iskanLoanAed}
          onMaxPriceChange={onMaxPriceChange}
          statusRowLayout={statusRowLayout}
          showPriceFilter={!hidePriceFilter}
        >
          <ListingStatusCardView
            reservedListingCard={reservedListingCard}
            isBookingLoading={isBookingLoading}
            isUpcomingLoading={isUpcomingLoading}
            appointment={appointment}
            relativeBadge={relativeBadge}
            isMobile={isMobile}
            isTablet={isTablet}
            isRTL={isRTL}
            statusRowLayout={statusRowLayout}
            colors={colors}
            onCancel={onCancel}
            onReschedule={onReschedule}
            onViewReservation={onViewReservation}
            onCancelReservation={onCancelReservation}
            canCancelReservedListing={canCancelReservedListing}
            isAnyDialogOpen={isAnyDialogOpen}
            matchedStatusCardMinHeight={matchedStatusCardMinHeight}
            onStatusCardsHeightChange={onStatusCardsHeightChange}
          />
        </AppointmentToolsRow>
      ) : null}
    </View>
  );
}

function PropertiesListGreetingRow({
  greeting,
  hideGreeting,
  showInlinePrice,
  showStatusRow,
  hidePriceFilter,
  isMobile,
  isTablet,
  isRTL,
  maxPrice,
  iskanLoanAed,
  onMaxPriceChange,
}: Readonly<{
  greeting: string;
  hideGreeting: boolean;
  showInlinePrice: boolean;
  showStatusRow: boolean;
  hidePriceFilter: boolean;
  isMobile: boolean;
  isTablet: boolean;
  isRTL: boolean;
  maxPrice: number;
  iskanLoanAed: number;
  onMaxPriceChange: (value: number) => void;
}>) {
  if (hideGreeting) {
    return null;
  }

  return (
    <View
      style={[
        styles.greetingRow,
        showInlinePrice && styles.greetingRowWithSlider,
        isMobile && showInlinePrice && { justifyContent: 'center' },
      ]}
    >
      <Typography
        color="primary"
        style={{
          fontSize: isMobile ? 22 : 28,
          fontWeight: '500',
          lineHeight: isMobile ? 22 : 28,
          fontFamily: 'Matter-Medium',
          flex: showStatusRow || hidePriceFilter ? undefined : 1,
          textAlign: isRTL ? 'right' : 'left',
        }}
      >
        {greeting}
      </Typography>

      {showInlinePrice ? (
        <View
          style={[styles.sliderWrap, isTablet && styles.sliderWrapTablet]}
        >
          <PriceRangeSlider
            value={maxPrice}
            loanAmountAed={iskanLoanAed}
            onValueChange={onMaxPriceChange}
            fullWidth={isTablet}
          />
        </View>
      ) : null}
    </View>
  );
}

const styles = StyleSheet.create({
  pageRoot: {
    flex: 1,
    minHeight: 0,
  },
  /**
   * Compact (phone/tablet tabs): do not add horizontal padding here —
   * `WebAppLayout` `shellMobile` already insets 16px. Stacking another 16
   * made iPhone SE content feel double-padded.
   */
  pageRootCompact: {
    paddingHorizontal: 0,
  },
  /**
   * Shared desktop row panes — Figma MAIN:SIDEBAR flex (~62% : 38%, ≈ 5:3).
   * Row 1: greeting | Explore Units
   * Row 2: status cards | price filter
   * Do not cap the right pane; maxWidth broke the ratio on wide screens.
   */
  alignRow: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 24,
    width: '100%',
  },
  /**
   * Status card | price filter: tops align; heights match on the card slot only.
   * Multi-card dots sit under the left card and add gap before the split below.
   */
  alignRowCardPrice: {
    alignItems: 'flex-start',
  },
  /**
   * ADHA desktop status | price: CSS grid equal-height row.
   * Row1 = card + price (stretch to max). Row2 = dots under card only.
   */
  cardPriceMatchGrid: {
    width: '100%',
  },
  cardPriceMatchGridInner: {
    width: '100%',
    display: 'grid',
    gridTemplateColumns: `${ADHA_LISTING_MAIN_FLEX}fr ${ADHA_LISTING_SIDEBAR_FLEX}fr`,
    gridTemplateRows: 'auto auto',
    columnGap: 24,
    alignItems: 'stretch',
  } as object,
  cardPriceMatchGridCard: {
    minWidth: 0,
    gridColumn: 1,
    gridRow: 1,
    display: 'flex',
    flexDirection: 'column',
    alignSelf: 'stretch',
    height: '100%',
  } as object,
  cardPriceMatchGridPrice: {
    minWidth: 0,
    gridColumn: 2,
    gridRow: 1,
    display: 'flex',
    flexDirection: 'column',
    alignSelf: 'stretch',
    height: '100%',
  } as object,
  cardPriceMatchGridPriceInner: {
    flex: 1,
    alignSelf: 'stretch',
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
  } as object,
  cardPriceMatchGridDots: {
    gridColumn: 1,
    gridRow: 2,
  } as object,
  /**
   * Zero-height anchor under the price card: the filters card hangs out of the
   * grid so row2 (and the left column) keeps its height.
   */
  cardPriceMatchGridBelowPrice: {
    minWidth: 0,
    gridColumn: 2,
    gridRow: 2,
    alignSelf: 'start',
    height: 0,
    position: 'relative',
    zIndex: 3,
  } as object,
  belowPriceOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
  },
  /** Bottom half of the price card: no top border/radius, divider inset like the price padding. */
  unitsFiltersCard: {
    width: '100%',
    borderWidth: 1,
    borderTopWidth: 0,
    borderBottomLeftRadius: 12,
    borderBottomRightRadius: 12,
    paddingHorizontal: 16,
    paddingBottom: 16,
    gap: 16,
  },
  unitsFiltersDivider: {
    height: 1,
    width: '100%',
  },
  /** Side-by-side status | price: stretch both panes to the same height. */
  alignRowMatchHeights: {
    alignItems: 'stretch',
    display: 'flex',
    flexDirection: 'row',
  } as object,
  alignLeft: {
    flexGrow: ADHA_LISTING_MAIN_FLEX,
    flexShrink: 1,
    flexBasis: 0,
    minWidth: 0,
  },
  alignRight: {
    flexGrow: ADHA_LISTING_SIDEBAR_FLEX,
    flexShrink: 1,
    flexBasis: 0,
    minWidth: 0,
  },
  alignPaneMatchHeights: {
    alignSelf: 'flex-start',
    display: 'flex',
    flexDirection: 'column',
  } as object,
  /** Card track only — shared height with price filter (dots excluded). */
  carouselCardsMatchSlot: {
    width: '100%',
    alignSelf: 'stretch',
    display: 'flex',
    flexDirection: 'column',
    flexGrow: 1,
  } as object,
  carouselCardsMatchSlotGrid: {
    flex: 1,
    height: '100%',
    minHeight: '100%',
  } as object,
  projectColumn: {
    width: '100%',
    gap: 16,
  },
  unitsColumn: {
    width: '100%',
    gap: 16,
  },
  unitsPriceFilter: {
    width: '100%',
    gap: 12,
  },
  unitsPriceFilterMatchHeights: {
    flex: 1,
    alignSelf: 'stretch',
    display: 'flex',
    flexDirection: 'column',
    flexGrow: 1,
  } as object,
  unitsCountBelowFilter: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 8,
    width: '100%',
    minHeight: 48,
    padding: 12,
    borderRadius: 8,
  },
  unitsCountLabel: {
    letterSpacing: 0,
    flexShrink: 1,
    fontWeight: '500',
  },
  unitsCountValueRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    flexShrink: 0,
  },
  unitsCountValue: {
    letterSpacing: 0,
    fontWeight: '500',
  },
  /** Full width of left pane (= carousel / project column). */
  statusCardsFullWidth: {
    width: '100%',
    maxWidth: '100%',
    alignSelf: 'stretch',
  },
  statusCardsMatchHeights: {
    flex: 1,
    alignSelf: 'stretch',
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
  } as object,
  headerBlock: {
    marginBottom: 0,
    gap: 0,
    width: '100%',
  },
  /** Status cards only (greeting lives in title row above). */
  headerBlockStatusOnly: {
    marginBottom: 0,
    gap: 0,
  },
  /** Fill the desktop left pane so cards can stretch with the price filter. */
  headerBlockMatchHeights: {
    flex: 1,
    alignSelf: 'stretch',
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
  } as object,
  greetingRow: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    width: '100%',
  },
  greetingRowWithSlider: {
    justifyContent: 'space-between',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: 24,
  },
  /** Appointment card + price filter share one row; non-mobile stretches both to equal height. */
  toolsRow: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    flexWrap: 'wrap',
    gap: 24,
    width: '100%',
  },
  /** Status row (tablet + desktop): stretch both columns to the same height. */
  toolsRowMatchHeights: {
    alignItems: 'stretch',
  },
  /** Fill parent pane when status cards sit beside an external price filter. */
  toolsRowFillParent: {
    flex: 1,
    alignSelf: 'stretch',
    height: '100%',
    display: 'flex',
  } as object,
  /** 1100–1320: fill the row with equal halves (no leftover empty gap). */
  toolsRowEqualSplit: {
    flexWrap: 'nowrap',
    justifyContent: 'flex-start',
  },
  /** Below 1100px: status card above price filter, each full width. */
  toolsRowStacked: {
    flexDirection: 'column',
    alignItems: 'stretch',
  },
  sliderWrap: {
    flexShrink: 1,
    maxWidth: '100%',
  },
  sliderWrapStacked: {
    width: '100%',
    alignSelf: 'stretch',
  },
  sliderWrapMatchHeights: {
    alignSelf: 'stretch',
  },
  /** Tablet: price filter shares the row equally with the status card (uniform halves). */
  sliderWrapTablet: {
    flexGrow: 1,
    flexShrink: 1,
    flexBasis: 0,
    minWidth: 280,
  },
  grid: {
    flexDirection: "row",
    flexWrap: "wrap",
    width: "100%",
    alignSelf: "stretch",
    gap: ADHA_UNITS_GRID_GAP,
  },
  viewportWindow: {
    width: "100%",
  },
  /** Shared by cards and their skeletons so both keep the same column edges. */
  gridItem: {
    maxWidth: "100%",
    alignSelf: "stretch",
    alignItems: "stretch",
    minWidth: 0,
    display: "flex",
    flexDirection: "column",
  } as object,
  floatingButtonContainer: {
    position: 'absolute',
    bottom: 32,
    right: 32,
    zIndex: 1000,
  },
  /** Web Arabic: same inset as LTR, on the opposite edge. */
  floatingButtonContainerWebRtl: {
    right: 'auto',
    left: 32,
  },
  floatingButtonContainerDocked: {
    bottom: 16,
  },
  /**
   * Pull past WebAppLayout `shellMobile` 16px inset so the chip sits on the
   * true screen edge (Figma: flat outer corner).
   */
  floatingButtonContainerDockedLTR: {
    right: -ADHA_LISTING_COMPACT_PADDING_X,
    left: 'auto',
    alignItems: 'flex-end',
  },
  floatingButtonContainerDockedRTL: {
    left: -ADHA_LISTING_COMPACT_PADDING_X,
    right: 'auto',
    alignItems: 'flex-start',
  },
  floatingButton: {
    display: 'flex',
    flexDirection: 'row',
    width: 270,
    height: 56,
    paddingVertical: 12,
    paddingHorizontal: 20,
    justifyContent: 'center',
    alignItems: 'center',
    gap: 12,
    borderRadius: 32,
    backgroundColor: '#1A1A1A',
    shadowColor: 'rgba(0, 0, 0, 0.62)',
    shadowOffset: { width: 0, height: 0 },
    shadowOpacity: 1,
    shadowRadius: 24,
    elevation: 10,
  },
  floatingButtonDocked: {
    width: 210,
    height: 48,
    paddingTop: 10,
    paddingRight: 16,
    paddingBottom: 10,
    paddingLeft: 16,
    gap: 10,
  },
  floatingButtonDockedLTR: {
    borderTopLeftRadius: 14,
    borderBottomLeftRadius: 14,
    borderTopRightRadius: 0,
    borderBottomRightRadius: 0,
    alignSelf: 'flex-end',
  },
  floatingButtonDockedRTL: {
    borderTopRightRadius: 14,
    borderBottomRightRadius: 14,
    borderTopLeftRadius: 0,
    borderBottomLeftRadius: 0,
    alignSelf: 'flex-start',
  },
  floatingButtonText: {
    color: '#FFFFFF',
    fontSize: 16,
    fontFamily: 'Matter-Medium',
    fontWeight: '500',
  },
  floatingButtonTextDocked: {
    fontSize: 14,
    lineHeight: 18,
  },
  /** Status + price row: fill the stretched column height beside the slider. */
  appointmentCard: {
    marginTop: 0,
    borderRadius: 12,
    borderWidth: 1,
    padding: 14,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    // Keep the details and the actions vertically centered whether the actions
    // sit on the same line or wrap to their own line.
    alignContent: 'center',
    gap: 16,
    flexWrap: 'wrap',
    alignSelf: 'stretch',
    maxWidth: 698,
    minWidth: 280,
    width: '100%',
    overflow: 'visible',
    zIndex: 1,
  },
  /** Side-by-side with price slider (≥1100): grow to match slider column height. */
  appointmentCardMatchHeights: {
    flexGrow: 1,
  },
  statusCardStatusRow: {
    flex: 1,
    alignSelf: 'stretch',
    height: '100%',
  },
  /** Carousel slot: appointment and reservation share the same outer box. */
  statusCardFillHeight: {
    flex: 1,
    alignSelf: 'stretch',
    width: '100%',
    height: '100%',
  },
  /** width > 1320: keep title + actions on one line; hug content height. */
  statusCardWideDesktop: {
    flexWrap: 'nowrap',
  },
  statusCardWideDesktopPadding: {
    paddingVertical: 12,
  },
  appointmentCardMobile: {
    marginTop: 0,
    maxWidth: '100%',
  },
  /** Side-by-side details | actions — summary wraps to 2 lines instead of pushing actions down. */
  appointmentCardActionsBeside: {
    flexWrap: 'nowrap',
    alignItems: 'center',
  },
  /**
   * Stacked (<1100): match listing grid width — drop the 698 desktop cap.
   * flexGrow fills the carousel slide so appointment + reservation cards share
   * the tallest card's height (slides are stretched by the horizontal track).
   */
  statusCardStackedFullWidth: {
    maxWidth: '100%',
    width: '100%',
    alignSelf: 'stretch',
    flexGrow: 1,
  },
  /** 1100–1320: fill equal half beside the price slider. */
  statusCardEqualSplitWidth: {
    maxWidth: '100%',
    width: '100%',
  },
  /** Figma reservation listing card — aligned with appointment card shell */
  reservedListingCard: {
    marginTop: 0,
    width: '100%',
    maxWidth: 698,
    borderRadius: 12,
    borderWidth: 1,
    padding: 14,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    alignContent: 'center',
    gap: 12,
    flexWrap: 'wrap',
    alignSelf: 'stretch',
  },
  /** Actions never wrap to a second line — scale size instead. */
  reservedCardActionsInline: {
    flexWrap: 'nowrap',
    flexShrink: 0,
  },
  /** View Reservation only — hug content instead of stretching across the tools row. */
  reservedListingCardCompact: {
    flex: 0,
    flexGrow: 0,
    flexShrink: 1,
    flexBasis: 'auto',
    alignSelf: 'flex-start',
    width: 'auto',
    maxWidth: '100%',
    justifyContent: 'flex-start',
    gap: 24,
  },
  reservedListingCardLeft: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    flexShrink: 1,
    flexGrow: 1,
    minWidth: 0,
  },
  appointmentLeft: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    flexGrow: 1,
    flexShrink: 1,
    flexBasis: 0,
    minWidth: 0,
    overflow: 'visible',
    zIndex: 2,
  },
  /** Mobile: stack meeting row under the icon+details so Join Here isn’t clipped. */
  appointmentLeftMobile: {
    flexDirection: 'column',
    alignItems: 'stretch',
    gap: 8,
  },
  appointmentLeftMain: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    flexGrow: 1,
    flexShrink: 1,
    minWidth: 0,
    width: '100%',
    overflow: 'visible',
  },
  appointmentIconCircle: {
    width: 40,
    height: 40,
    borderRadius: 20,
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  appointmentDetails: {
    gap: 10,
    justifyContent: 'center',
    flexGrow: 1,
    flexShrink: 1,
    minWidth: 0,
    overflow: 'visible',
    zIndex: 2,
  },
  appointmentHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    flexWrap: 'nowrap',
    gap: 8,
    minHeight: 20,
  },
  appointmentTitle: {
    fontSize: 16,
    lineHeight: 20,
    fontFamily: 'Matter-Medium',
    fontWeight: '500',
    color: '#1A1A1A',
    flexShrink: 1,
  },
  tomorrowBadge: {
    paddingHorizontal: 8,
    paddingVertical: 2,
    borderRadius: 4,
    alignItems: 'center',
    justifyContent: 'center',
  },
  tomorrowText: {
    fontSize: 12,
    lineHeight: 16,
    fontFamily: 'Matter-Medium',
    fontWeight: '600',
  },
  appointmentSubtext: {
    fontSize: 14,
    lineHeight: 16,
    letterSpacing: 0,
    fontWeight: '400',
    fontFamily: 'Matter-Regular',
    color: '#4D4D4D',
  },
  /** Date / time / Sales on one flex row — bullets use padding, not spaces. */
  appointmentSummaryBlock: {
    width: '100%',
    minWidth: 0,
    gap: 8,
  },
  appointmentSummaryRow: {
    flexDirection: 'row',
    flexWrap: 'nowrap',
    alignItems: 'center',
    alignSelf: 'stretch',
    width: '100%',
    minWidth: 0,
    minHeight: 16,
    overflow: 'visible',
    zIndex: 2,
  },
  appointmentSalesNextLine: {
    width: '100%',
    minWidth: 0,
    alignSelf: 'stretch',
    flexDirection: 'row',
    alignItems: 'center',
  },
  appointmentSummaryBullet: {
    fontSize: 14,
    lineHeight: 16,
    fontFamily: 'Matter-Regular',
    fontWeight: '400',
    color: '#4D4D4D',
    paddingHorizontal: 8,
    flexShrink: 0,
  },
  appointmentSummary: {
    fontSize: 14,
    lineHeight: 16,
    letterSpacing: 0,
    fontWeight: '400',
    fontFamily: 'Matter-Regular',
    color: '#4D4D4D',
    flexShrink: 1,
    minWidth: 0,
    ...(Platform.OS === 'web' ? null : { includeFontPadding: false }),
  },
  appointmentMeetingRow: {
    flexDirection: 'row',
    flexWrap: 'nowrap',
    alignItems: 'center',
    alignSelf: 'stretch',
    width: '100%',
    minWidth: 0,
  },
  /** Off-screen probe for unwrapped single-line width. */
  appointmentMeetingMeasureProbe: {
    position: 'absolute',
    opacity: 0,
    left: 0,
    top: 0,
    zIndex: -1,
  },
  appointmentMeetingMeasureRow: {
    flexDirection: 'row',
    flexWrap: 'nowrap',
    alignItems: 'center',
  },
  appointmentMeetingMeasureText: {
    flexShrink: 0,
    minWidth: undefined,
  },
  /**
   * Figma divider: 17×0 line at -90° → vertical 17px bar, 1px solid font tertiary.
   */
  appointmentMeetingDivider: {
    width: 17,
    height: 0,
    borderTopWidth: 1,
    borderStyle: 'solid',
    opacity: 1,
    transform: [{ rotate: '-90deg' }],
    flexShrink: 0,
    marginHorizontal: 8,
  },
  appointmentJoinPressable: {
    flexShrink: 0,
  },
  /** Same LH as divider row so Visit/Join sit on one baseline. */
  appointmentJoinLink: {
    fontSize: 13,
    lineHeight: 18,
    letterSpacing: 0,
    fontWeight: '500',
    fontFamily: 'Matter-Medium',
  },
  /** Font/Size/2XS Regular, weight 400, LH 100% */
  listingMessage: {
    lineHeight: 13,
    letterSpacing: 0,
  },
  appointmentRight: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    flexShrink: 0,
  },
  /** Stay as a tight group; pin to trailing edge without stretching (stretch spreads buttons). */
  reservedCardActions: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 32,
    flexGrow: 0,
    flexShrink: 0,
  },
  reservedCardActionsEnd: {
    marginStart: 'auto',
  },
  /** <500px: Cancel on the left edge, outline action on the right edge. */
  reservedCardActionsMobileSpread: {
    width: '100%',
    flexGrow: 1,
    flexShrink: 1,
    justifyContent: 'space-between',
    gap: 12,
  },
  reservedCardActionsCompact: {
    marginStart: 'auto',
    gap: 32,
  },
  /** Wide desktop: still hug content — do not flex-grow the action row. */
  appointmentRightEndWide: {
    marginStart: 'auto',
    gap: 32,
  },
  reservedCancelBtn: {
    justifyContent: 'center',
    alignItems: 'center',
    flexShrink: 0,
    paddingHorizontal: 4,
  },
  reservedCancelBtnCompact: {
    paddingHorizontal: 2,
  },
  reservedCancelBtnTight: {
    paddingHorizontal: 0,
  },
  /** Appointment card mobile — shorter Cancel / Reschedule labels. */
  reservedCancelBtnMobile: {
    flexShrink: 1,
    minWidth: 0,
    paddingHorizontal: 0,
  },
  reservedCancelText: {
    lineHeight: 16,
    letterSpacing: 0,
    textAlign: 'center',
    textDecorationLine: 'underline',
    textDecorationStyle: 'solid',
    paddingBottom: 2,
    ...( {
      textUnderlineOffset: 4,
      WebkitFontSmoothing: 'antialiased',
    } as object),
  },
  reservedCancelTextCompact: {
    fontSize: 13,
    lineHeight: 15,
  },
  reservedCancelTextTight: {
    fontSize: 12,
    lineHeight: 14,
  },
  reservedCancelTextMobile: {
    textAlign: 'center',
  },
  /** Figma base 154×49 — min width grows so label stays on one line */
  viewReservationBtn: {
    minWidth: 154,
    height: 49,
    borderRadius: 4,
    borderWidth: 2,
    paddingHorizontal: 12,
    paddingVertical: 12,
    justifyContent: 'center',
    alignItems: 'center',
    flexShrink: 0,
  },
  viewReservationBtnCompact: {
    minWidth: 118,
    height: 40,
    paddingHorizontal: 10,
    paddingVertical: 8,
    borderWidth: 1.5,
  },
  viewReservationBtnTight: {
    minWidth: 96,
    height: 36,
    paddingHorizontal: 8,
    paddingVertical: 6,
    borderWidth: 1.5,
  },
  /** Appointment card mobile reschedule control. */
  viewReservationBtnMobile: {
    minWidth: 124,
    height: 44,
    paddingHorizontal: 10,
    paddingVertical: 10,
    flexShrink: 0,
  },
  viewReservationText: {
    lineHeight: 16,
    letterSpacing: 0,
    textAlign: 'center',
    flexShrink: 0,
    ...( { WebkitFontSmoothing: 'antialiased' } as object),
  },
  viewReservationTextCompact: {
    fontSize: 13,
    lineHeight: 15,
  },
  viewReservationTextTight: {
    fontSize: 12,
    lineHeight: 14,
  },
  carouselContainer: {
    maxWidth: 698,
    minWidth: 280,
    alignSelf: 'stretch',
  },
  carouselContainerMatchHeights: {
    flex: 1,
    alignSelf: 'stretch',
    display: 'flex',
    flexDirection: 'column',
  } as object,
  carouselContainerStacked: {
    width: '100%',
    maxWidth: '100%',
    alignSelf: 'stretch',
  },
  /** 1100–1320: share the tools row equally with the price slider. */
  carouselContainerEqualSplit: {
    flex: 1,
    flexGrow: 1,
    flexShrink: 1,
    flexBasis: 0,
    maxWidth: '100%',
    minWidth: 0,
  },
  carouselCardWrapMatchHeights: {
    flex: 1,
    alignSelf: 'stretch',
    height: '100%',
  },
  /**
   * RN-web pagingEnabled wraps each slide in a snap View that is stretched to
   * the tallest slide; growing here lets the card fill that height.
   */
  carouselCardWrapGrow: {
    flexGrow: 1,
  },
  carouselSingleCardSlot: {
    flex: 1,
    alignSelf: 'stretch',
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
  } as object,
  carouselScrollView: {
    overflow: 'hidden',
    flex: 1,
    alignSelf: 'stretch',
  },
  /** Stacked (<1100): don't stretch the scrollview to fill empty vertical space. */
  carouselScrollViewStacked: {
    overflow: 'hidden',
  },
  /** Before onLayout measures width, avoid width:0 squeezing card text vertically. */
  carouselCardMeasured: {
    width: '100%',
    alignSelf: 'stretch',
  },
  dotsContainer: {
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
    marginTop: 8,
    gap: 8,
    height: STATUS_CAROUSEL_DOTS_BLOCK_HEIGHT - 8,
  },
  /** Larger hit target; visual bar is the inner View. */
  statusCarouselDotHit: {
    paddingVertical: 6,
    paddingHorizontal: 2,
    justifyContent: 'center',
    alignItems: 'center',
  },
  /**
   * Figma vector ~29×4, borderRadius 4 (full capsule on 4px height).
   */
  statusCarouselDot: {
    width: 29,
    height: 4,
    borderRadius: 4,
    borderTopLeftRadius: 4,
    borderTopRightRadius: 4,
    borderBottomLeftRadius: 4,
    borderBottomRightRadius: 4,
    overflow: 'hidden',
    backgroundColor: '#D0D0D0',
  },
  statusCarouselDotActive: {
    borderRadius: 4,
    borderTopLeftRadius: 4,
    borderTopRightRadius: 4,
    borderBottomLeftRadius: 4,
    borderBottomRightRadius: 4,
    overflow: 'hidden',
    backgroundColor: '#000000',
  },
  dot: {
    width: 8,
    height: 8,
    borderRadius: 4,
    backgroundColor: '#CCCCCC',
  },
  activeDot: {
    backgroundColor: '#1A1A1A',
  },
});
